import {
  COLORS,
  type GameState,
  type GameStatus,
  type Pos,
  isPathComplete,
  posEqual,
} from '@color-dots/game';
import { isDarkTheme } from '../theme.js';
import { drawGearRimSprite } from './gearRimSprites.js';
import type { GearSoundEvent } from './gearSound.js';
import { drawCompletedDotFx, drawCompletedPathFx } from './completedFx.js';
import { type VisualStyle } from './visualStyles.js';

export type GearSoundCallback = (event: GearSoundEvent) => void;

interface GearSegmentAnim {
  color: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  type: 'extend' | 'backtrack';
  startMs: number;
  durationMs: number;
}

interface GearTimedAnim {
  startMs: number;
  durationMs: number;
}

interface GearPairCompleteAnim extends GearTimedAnim {
  color: string;
}

interface GearErrorAnim extends GearTimedAnim {
  color: string;
}

export interface HintOverlay {
  color: string;
  /** Unplayed solution cells to trace, beyond the player's current path. */
  cells: Pos[];
  /** True when the player's own path for this color must be undone before it can be hinted. */
  conflict?: boolean;
}

export interface RenderOptions {
  padding?: number;
  backgroundColor?: string;
  dotScale?: number;
  visualStyle?: VisualStyle;
  /** Word Connect: words in cells, no colored endpoint dots */
  wordMode?: boolean;
}

const COLOR_LIGHT: Record<string, string> = {
  c1: '#FCA5A5',
  c2: '#93C5FD',
  c3: '#86EFAC',
  c4: '#FCD34D',
  c5: '#D8B4FE',
  c6: '#F2A6DE',
  c7: '#5EEAD4',
  c8: '#BEF264',
  c9: '#F9B494',
  c10: '#C5C2FA',
};

export class CanvasRenderer {
  private ctx: CanvasRenderingContext2D;
  private cellSize = 0;
  private padding = 20;
  /** Shrinks the board's inner margin on big grids so cells stay as large as possible. */
  private autoPadding = true;
  private dotScale = 0.38;
  private pipeRatio = 0.48;
  private visualStyle: VisualStyle = 'pipe';
  private wordMode = false;
  private prevPaths = new Map<string, Pos[]>();
  private prevPairComplete = new Map<string, boolean>();
  /** Cells on a completed chain → colorId, rebuilt each gear render. Drives continuous spin. */
  private connectedCells = new Map<string, string>();
  private prevStatus: GameStatus = 'playing';
  private segmentAnim: GearSegmentAnim | null = null;
  private pairCompleteAnim: GearPairCompleteAnim | null = null;
  private winAnim: GearTimedAnim | null = null;
  private errorAnim: GearErrorAnim | null = null;
  private hint: HintOverlay | null = null;
  private renderCallback: (() => void) | null = null;
  private gearSoundCallback: GearSoundCallback | null = null;
  private redrawScheduled = false;
  private styleDecl: CSSStyleDeclaration | null = null;

  /**
   * Queues at most one animation frame at a time. Without this guard each
   * `render()` could schedule several rAF callbacks, each of which triggers
   * another `render()` — making the number of pending frames grow
   * exponentially and freezing the page.
   */
  private scheduleRedraw(): void {
    if (this.redrawScheduled || !this.renderCallback) return;
    this.redrawScheduled = true;
    requestAnimationFrame(() => {
      this.redrawScheduled = false;
      this.renderCallback?.();
    });
  }

  constructor(
    private canvas: HTMLCanvasElement,
    options: RenderOptions = {},
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.ctx = ctx;
    if (options.padding !== undefined) {
      this.padding = options.padding;
      this.autoPadding = false;
    }
    if (options.dotScale !== undefined) this.dotScale = options.dotScale;
    if (options.visualStyle !== undefined) this.visualStyle = options.visualStyle;
    if (options.wordMode) this.wordMode = true;
  }

  getCellSize(): number {
    return this.cellSize;
  }

  setVisualStyle(style: VisualStyle): void {
    this.visualStyle = style;
  }

  setRenderCallback(callback: (() => void) | null): void {
    this.renderCallback = callback;
  }

  setGearSoundCallback(callback: GearSoundCallback | null): void {
    this.gearSoundCallback = callback;
  }

  setHint(hint: HintOverlay | null): void {
    this.hint = hint;
    this.scheduleRedraw();
  }

  private emitGearSound(event: GearSoundEvent): void {
    if (this.visualStyle !== 'gear') return;
    this.gearSoundCallback?.(event);
  }

  /** Test hook for gear path animation progress (0–1), null when idle. */
  getGearAnimProgress(): number | null {
    if (!this.segmentAnim) return null;
    return this.getAnimProgress();
  }

  /** Test hook: active endpoint/path pulse scale (1..1.06), 1 when reduced-motion. */
  getGearPulseScale(): number {
    return this.computeGearPulseScale();
  }

  /** Test hook: pair-complete animation progress (0–1), null when idle. */
  getGearPairCompleteProgress(): number | null {
    if (!this.pairCompleteAnim) return null;
    return this.getTimedAnimProgress(this.pairCompleteAnim, 0);
  }

  /** Test hook: win-wave animation progress (0–1), null when idle. */
  getGearWinProgress(): number | null {
    if (!this.winAnim) return null;
    return this.getTimedAnimProgress(this.winAnim, 0);
  }

  /** Test hook: invalid-move feedback progress (0–1), null when idle. */
  getGearErrorProgress(): number | null {
    if (!this.errorAnim) return null;
    return Math.min(1, (performance.now() - this.errorAnim.startMs) / this.errorAnim.durationMs);
  }

  /** Test hook: gear rotation (radians) for a grid cell during effect anims. */
  getGearCellRotation(state: GameState, x: number, y: number): number {
    return this.computeGearRotation(state, x, y);
  }

  /** Test hook: idle gear rotation speed (deg/s) for empty cells, 0 when disabled. */
  getGearIdleRotationDegPerSec(x: number, y: number): number {
    if (!this.gearAnimationsEnabled()) return 0;
    return 2 + ((x * 7 + y * 11) % 3);
  }

  /** Test hook: L0 wall parallax drift (px) for a given board size. */
  getGearWallParallaxOffset(boardSize: number): { x: number; y: number } {
    return this.computeGearWallParallaxOffset(boardSize);
  }

  notifyGearInvalidMove(color: string): void {
    if (!this.gearAnimationsEnabled()) return;
    this.errorAnim = { color, startMs: performance.now(), durationMs: 400 };
    this.scheduleRedraw();
  }

  resize(state: GameState, maxSize: number): void {
    const n = state.size;
    if (this.autoPadding) this.padding = n >= 9 ? 6 : n >= 7 ? 10 : n >= 6 ? 14 : 20;
    this.cellSize = Math.floor((maxSize - this.padding * 2) / n);
    const boardSize = this.cellSize * n + this.padding * 2;

    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = boardSize * dpr;
    this.canvas.height = boardSize * dpr;
    this.canvas.style.width = `${boardSize}px`;
    this.canvas.style.height = `${boardSize}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  posToPixel(pos: Pos): { x: number; y: number } {
    const [gx, gy] = pos;
    return {
      x: this.padding + gx * this.cellSize + this.cellSize / 2,
      y: this.padding + gy * this.cellSize + this.cellSize / 2,
    };
  }

  pixelToPos(x: number, y: number, size?: number): Pos | null {
    const gx = Math.floor((x - this.padding) / this.cellSize);
    const gy = Math.floor((y - this.padding) / this.cellSize);
    if (gx < 0 || gy < 0) return null;
    if (size !== undefined && (gx >= size || gy >= size)) return null;
    return [gx, gy];
  }

  getBoardSize(): number {
    return this.cellSize * 0 + this.padding * 2 + this.cellSize * 1;
  }

  render(state: GameState, activeColor: string | null = null): void {
    const { ctx } = this;
    const n = state.size;
    const boardSize = this.cellSize * n + this.padding * 2;

    ctx.clearRect(0, 0, boardSize, boardSize);
    this.drawBoardBackground(boardSize);
    this.updateGearPathAnim(state);
    this.updateGearEffectAnims(state);

    if (this.visualStyle === 'gear') {
      this.drawGearRails(state);
      this.drawGearCellGears(state, activeColor);
    } else {
      this.drawEmptyCells(state);
    }

    this.drawPaths(state, activeColor);

    if (!this.wordMode) {
      for (const [color, [a, b]] of state.endpoints) {
        const isActive = color === activeColor;
        this.drawDot(a, color, isActive, state);
        this.drawDot(b, color, isActive, state);
      }
    }

    this.drawHintOverlay(state);

    this.scheduleGearPulseRedraw(activeColor, state);
    this.scheduleGearEffectRedraw();
    this.scheduleGearIdleRedraw(state);
    this.scheduleGearParallaxRedraw();
    this.scheduleHintRedraw();
  }

  private scheduleHintRedraw(): void {
    if (this.hint && this.gearAnimationsEnabled() && this.renderCallback) {
      this.scheduleRedraw();
    }
  }

  private drawHintOverlay(state: GameState): void {
    if (!this.hint) return;
    const { color, cells, conflict } = this.hint;
    const pulse = this.computeGearPulseScale();
    const pulsePhase = this.computeGearPulsePhase();

    if (conflict) {
      this.drawHintConflict(state, color, pulse, pulsePhase);
      return;
    }

    const { ctx } = this;
    const base = COLORS[color] ?? '#888';
    const endpoints = state.endpoints.get(color);

    if (endpoints) {
      for (const pos of endpoints) {
        const { x, y } = this.posToPixel(pos);
        const r = (this.cellSize * this.dotScale * 0.62 + this.cellSize * 0.16) * pulse;
        ctx.save();
        ctx.globalAlpha = 0.55 + pulsePhase * 0.45;
        ctx.shadowColor = base;
        ctx.shadowBlur = this.cellSize * 0.45 * pulse;
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = Math.max(3, this.cellSize * 0.09);
        ctx.setLineDash([this.cellSize * 0.12, this.cellSize * 0.09]);
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      }
    }

    if (cells.length === 0) return;

    const path = state.paths.get(color) ?? [];
    const from = path.length > 0 ? path[path.length - 1] : (endpoints?.[0] ?? cells[0]);
    const points = posEqual(from, cells[0]) ? cells : [from, ...cells];

    ctx.save();
    ctx.globalAlpha = 0.45 + pulsePhase * 0.2;
    ctx.strokeStyle = base;
    ctx.lineWidth = Math.max(2, this.cellSize * 0.16);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([this.cellSize * 0.16, this.cellSize * 0.14]);
    ctx.beginPath();
    points.forEach((p, i) => {
      const { x, y } = this.posToPixel(p);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  /** Flags the player's own drawn path for `color` as the thing to undo — it can't be
   *  extended into the level's solution as-is. */
  private drawHintConflict(
    state: GameState,
    color: string,
    pulse: number,
    pulsePhase: number,
  ): void {
    const { ctx } = this;
    const errorColor = '#EF4444';
    const endpoints = state.endpoints.get(color);
    const path = state.paths.get(color) ?? [];

    if (endpoints) {
      for (const pos of endpoints) {
        const { x, y } = this.posToPixel(pos);
        const r = (this.cellSize * this.dotScale * 0.62 + this.cellSize * 0.16) * pulse;
        ctx.save();
        ctx.globalAlpha = 0.55 + pulsePhase * 0.4;
        ctx.strokeStyle = errorColor;
        ctx.lineWidth = Math.max(3, this.cellSize * 0.08);
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }

    if (path.length < 2) return;

    ctx.save();
    ctx.globalAlpha = 0.5 + pulsePhase * 0.35;
    ctx.strokeStyle = errorColor;
    ctx.lineWidth = Math.max(3, this.cellSize * 0.18);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([this.cellSize * 0.14, this.cellSize * 0.1]);
    ctx.beginPath();
    path.forEach((p, i) => {
      const { x, y } = this.posToPixel(p);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  private computeGearPulseScale(amplitude = 0.06): number {
    if (!this.gearAnimationsEnabled()) return 1;
    const phase = (Math.sin(performance.now() / 420) + 1) / 2;
    return 1 + phase * amplitude;
  }

  private computeGearPulsePhase(): number {
    if (!this.gearAnimationsEnabled()) return 0;
    return (Math.sin(performance.now() / 420) + 1) / 2;
  }

  /** Keeps re-arming the redraw loop for as long as the active drag OR any
   *  already-completed pair needs its continuous pulse (see `drawDot`'s
   *  `isPairComplete` — added 2026-09-26 alongside this check) — otherwise a
   *  finished pair's dots would only animate on the single frame each pointer
   *  event happens to trigger, not continuously. */
  private scheduleGearPulseRedraw(activeColor: string | null, state: GameState): void {
    if (!this.gearAnimationsEnabled() || !this.renderCallback) return;
    if (activeColor) {
      this.scheduleRedraw();
      return;
    }
    for (const [color, endpoints] of state.endpoints) {
      if (isPathComplete(state.paths.get(color), endpoints)) {
        this.scheduleRedraw();
        return;
      }
    }
  }

  private scheduleGearEffectRedraw(): void {
    if (!this.gearAnimationsEnabled() || !this.renderCallback) {
      return;
    }
    const active =
      (this.pairCompleteAnim &&
        this.getTimedAnimProgress(this.pairCompleteAnim, 0) < 1) ||
      (this.winAnim && this.getTimedAnimProgress(this.winAnim, 0) < 1) ||
      (this.errorAnim && this.getGearErrorProgress()! < 1);
    if (active) {
      this.scheduleRedraw();
    }
  }

  private scheduleGearIdleRedraw(state: GameState): void {
    if (this.visualStyle !== 'gear' || !this.gearAnimationsEnabled() || !this.renderCallback) {
      return;
    }
    if (this.connectedCells.size > 0) {
      this.scheduleRedraw();
      return;
    }
    for (let y = 0; y < state.size; y++) {
      for (let x = 0; x < state.size; x++) {
        if (state.grid[y]![x] === null && !this.isEndpointAt(state, x, y)) {
          this.scheduleRedraw();
          return;
        }
      }
    }
  }

  private scheduleGearParallaxRedraw(): void {
    if (
      this.visualStyle !== 'gear' ||
      !isDarkTheme() ||
      !this.gearAnimationsEnabled() ||
      !this.renderCallback
    ) {
      return;
    }
    this.scheduleRedraw();
  }

  /** Tracks pair-complete/win/error animation state — originally gear-only,
   *  generalized 2026-09-25 alongside `updateGearPathAnim` so every style gets
   *  the same endpoint flash/shake feedback (see `drawDot`'s generic branch).
   *  `connectedCells` (drives gear's continuous chain-spin) stays gear-only —
   *  no other style has a "spinning" concept for it to feed. */
  private updateGearEffectAnims(state: GameState): void {
    this.connectedCells.clear();
    if (this.visualStyle === 'gear') {
      for (const [color, endpoints] of state.endpoints) {
        const path = state.paths.get(color);
        if (path && isPathComplete(path, endpoints)) {
          for (const [px, py] of path) {
            this.connectedCells.set(`${px},${py}`, color);
          }
        }
      }
    }

    for (const [color, endpoints] of state.endpoints) {
      const complete = isPathComplete(state.paths.get(color), endpoints);
      const wasComplete = this.prevPairComplete.get(color) ?? false;
      if (
        complete &&
        !wasComplete &&
        state.status !== 'won'
      ) {
        if (this.gearAnimationsEnabled()) {
          this.pairCompleteAnim = {
            color,
            startMs: performance.now(),
            durationMs: 300,
          };
        }
        this.emitGearSound({ kind: 'pairComplete' });
      }
      this.prevPairComplete.set(color, complete);
    }

    if (state.status === 'won' && this.prevStatus !== 'won') {
      if (this.gearAnimationsEnabled()) {
        this.winAnim = { startMs: performance.now(), durationMs: 800 };
      }
      this.emitGearSound({ kind: 'win' });
    }
    this.prevStatus = state.status;

    if (
      this.pairCompleteAnim &&
      this.getTimedAnimProgress(this.pairCompleteAnim, 0) >= 1
    ) {
      this.pairCompleteAnim = null;
    }
    if (this.winAnim && this.getTimedAnimProgress(this.winAnim, 0) >= 1) {
      this.winAnim = null;
    }
    if (this.errorAnim && this.getGearErrorProgress()! >= 1) {
      this.errorAnim = null;
    }
  }

  private getTimedAnimProgress(anim: GearTimedAnim, delayFraction: number): number {
    const delayMs = anim.durationMs * delayFraction;
    const t = (performance.now() - anim.startMs - delayMs) / anim.durationMs;
    if (t <= 0) return 0;
    return Math.min(1, 1 - Math.pow(1 - t, 3));
  }

  private computeGearRotation(state: GameState, x: number, y: number): number {
    if (!this.gearAnimationsEnabled()) return 0;

    const n = state.size;
    const centerX = (n - 1) / 2;
    const centerY = (n - 1) / 2;

    if (this.winAnim) {
      const dist = Math.hypot(x - centerX, y - centerY);
      const maxDist = Math.hypot(centerX, centerY) || 1;
      const delay = (dist / maxDist) * 0.45;
      const t = this.getTimedAnimProgress(this.winAnim, delay);
      if (t > 0) return t * (Math.PI / 2);
    }

    if (this.pairCompleteAnim) {
      const { color } = this.pairCompleteAnim;
      const path = state.paths.get(color);
      const endpoints = state.endpoints.get(color);
      const onPath = path?.some(([px, py]) => px === x && py === y);
      const onEndpoint =
        endpoints &&
        (posEqual(endpoints[0], [x, y]) || posEqual(endpoints[1], [x, y]));
      if (onPath || onEndpoint) {
        const dir = this.connectedSpinDirection(color);
        return dir * this.getTimedAnimProgress(this.pairCompleteAnim, 0) * (Math.PI / 2);
      }
    }

    // Once a pair is connected its chain keeps the gears turning, like a
    // powered drivetrain. A whole chain spins together in one direction
    // (chain-drive sprockets co-rotate); separate chains may differ.
    const connectedColor = this.connectedCells.get(`${x},${y}`);
    if (connectedColor) {
      return this.computeConnectedSpin(connectedColor);
    }

    if (
      state.grid[y]?.[x] === null &&
      !this.isEndpointAt(state, x, y)
    ) {
      return this.computeIdleGearRotation(x, y);
    }

    return 0;
  }

  private connectedSpinDirection(colorId: string): number {
    const idx = Number.parseInt(colorId.replace(/\D/g, ''), 10) || 1;
    return idx % 2 === 1 ? 1 : -1;
  }

  private computeConnectedSpin(colorId: string): number {
    if (!this.gearAnimationsEnabled()) return 0;
    const dir = this.connectedSpinDirection(colorId);
    const degPerSec = 70;
    const t = performance.now() / 1000;
    return dir * ((degPerSec * Math.PI) / 180) * t;
  }

  private computeGearWallParallaxOffset(boardSize: number): { x: number; y: number } {
    if (!this.gearAnimationsEnabled()) {
      return { x: 0, y: 0 };
    }
    const t = performance.now() / 1000;
    return {
      x: Math.sin(t * 0.12) * boardSize * 0.012,
      y: Math.cos(t * 0.09) * boardSize * 0.01,
    };
  }

  private computeIdleGearRotation(x: number, y: number): number {
    const degPerSec = this.getGearIdleRotationDegPerSec(x, y);
    if (degPerSec === 0) return 0;
    const dir = (x + y) % 2 === 0 ? 1 : -1;
    const t = performance.now() / 1000;
    return dir * ((degPerSec * Math.PI) / 180) * t;
  }

  private getEndpointFlashMul(colorId: string): number {
    let mul = 1;

    if (this.pairCompleteAnim?.color === colorId) {
      const t = this.getTimedAnimProgress(this.pairCompleteAnim, 0);
      mul = Math.max(mul, 1 + Math.sin(t * Math.PI) * 0.35);
    }

    if (this.winAnim) {
      const t = Math.min(1, (performance.now() - this.winAnim.startMs) / 400);
      mul = Math.max(mul, 1 + (1 - t) * 0.55 * Math.sin(t * Math.PI));
    }

    return mul;
  }

  private getErrorVisuals(colorId: string): {
    dx: number;
    dy: number;
    dim: number;
    chainFade: number;
  } | null {
    if (!this.errorAnim || this.errorAnim.color !== colorId || !this.gearAnimationsEnabled()) {
      return null;
    }
    const t = this.getGearErrorProgress() ?? 1;
    if (t >= 1) return null;
    const shake = Math.sin(t * Math.PI * 7) * (1 - t) * this.cellSize * 0.028;
    const chainFade = 1 - Math.min(1, t * 2.2);
    const dim = 0.5 + 0.5 * t;
    return { dx: shake, dy: shake * 0.35, dim, chainFade };
  }

  private drawBoardBackground(boardSize: number): void {
    const { ctx } = this;
    const r = 14;

    ctx.save();
    this.roundRect(ctx, 0, 0, boardSize, boardSize, r);
    ctx.clip();

    if (this.visualStyle === 'gear') {
      const cx = boardSize * 0.42;
      const cy = boardSize * 0.38;
      const bg = ctx.createRadialGradient(cx, cy, 0, boardSize / 2, boardSize / 2, boardSize * 0.78);
      bg.addColorStop(0, this.themeColor('--gear-scene-bg-stop', '#12121c'));
      bg.addColorStop(1, this.themeColor('--gear-scene-bg', '#0a0a12'));
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, boardSize, boardSize);

      if (isDarkTheme()) {
        this.drawGearWallPattern(boardSize);
        this.drawGearVignette(boardSize);
      }
    } else {
      const bg = ctx.createLinearGradient(0, 0, boardSize, boardSize);
      bg.addColorStop(0, this.themeColor('--canvas-bg-stop', '#FFFFFF'));
      bg.addColorStop(1, this.themeColor('--canvas-bg', '#FAFAFC'));
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, boardSize, boardSize);

      const vig = ctx.createRadialGradient(
        boardSize / 2,
        boardSize / 2,
        boardSize * 0.35,
        boardSize / 2,
        boardSize / 2,
        boardSize * 0.72,
      );
      vig.addColorStop(0, 'rgba(0, 0, 0, 0)');
      vig.addColorStop(1, this.themeColor('--canvas-vignette', isDarkTheme() ? 'rgba(0, 0, 0, 0.28)' : 'rgba(0, 0, 0, 0.06)'));
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, boardSize, boardSize);
    }

    ctx.restore();
  }

  private drawGearWallPattern(boardSize: number): void {
    const { ctx } = this;
    const brass = this.themeColor('--gear-wall-brass', '#8a6a3a');
    const steel = this.themeColor('--gear-wall-steel', '#555568');
    const count = Math.max(24, Math.floor((boardSize * boardSize) / 900));
    const { x: driftX, y: driftY } = this.computeGearWallParallaxOffset(boardSize);

    ctx.save();

    for (let i = 0; i < count; i++) {
      const seed = i * 7919 + 13;
      const depth = 0.45 + (seed % 6) / 10;
      const baseX = ((seed * 17) % 997) / 997 * boardSize;
      const baseY = ((seed * 31) % 991) / 991 * boardSize;
      const x = baseX + driftX * depth;
      const y = baseY + driftY * depth;
      const outerR = 3 + (seed % 7);
      const innerR = outerR * 0.62;
      const teeth = 6 + (seed % 5);
      const color = i % 3 === 0 ? brass : steel;
      const alpha = 0.1 + (seed % 12) / 100;

      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.beginPath();
      for (let t = 0; t < teeth * 2; t++) {
        const angle = (t * Math.PI) / teeth;
        const r = t % 2 === 0 ? outerR : innerR;
        const px = x + Math.cos(angle) * r;
        const py = y + Math.sin(angle) * r;
        if (t === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  }

  private drawGearVignette(boardSize: number): void {
    const { ctx } = this;
    const cx = boardSize / 2;
    const vig = this.themeColor('--gear-vignette', 'rgba(0, 0, 0, 0.55)');
    const grad = ctx.createRadialGradient(cx, cx, boardSize * 0.22, cx, cx, boardSize * 0.72);
    grad.addColorStop(0, 'rgba(0, 0, 0, 0)');
    grad.addColorStop(1, vig);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, boardSize, boardSize);
  }

  private gearAnimationsEnabled(): boolean {
    if (document.documentElement.dataset.animations === 'off') return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return true;
  }

  /** Grows/shrinks the most-recently-changed segment over a short tween instead
   *  of popping it in/out instantly. Originally gear-only; generalized 2026-09-25
   *  (user: "animation in every level like with gears") — the detection/easing
   *  here was never actually gear-specific, only its consumer (`drawPaths`, via
   *  `drawStyledSegmentAnimated`) needed to learn how to animate non-gear styles. */
  private updateGearPathAnim(state: GameState): void {
    if (this.segmentAnim && this.getAnimProgress() >= 1) {
      this.segmentAnim = null;
    }

    if (this.gearAnimationsEnabled() && !this.segmentAnim) {
      for (const [color, path] of state.paths) {
        const prev = this.prevPaths.get(color) ?? [];
        if (path.length > prev.length && path.length >= 2) {
          const [x1, y1] = path[path.length - 2]!;
          const [x2, y2] = path[path.length - 1]!;
          this.segmentAnim = {
            color,
            x1,
            y1,
            x2,
            y2,
            type: 'extend',
            startMs: performance.now(),
            durationMs: 150,
          };
          break;
        }
        if (path.length < prev.length && prev.length >= 2) {
          const [x1, y1] = prev[prev.length - 2]!;
          const [x2, y2] = prev[prev.length - 1]!;
          this.segmentAnim = {
            color,
            x1,
            y1,
            x2,
            y2,
            type: 'backtrack',
            startMs: performance.now(),
            durationMs: 80,
          };
          break;
        }
      }
    }

    for (const [color, path] of state.paths) {
      const prev = this.prevPaths.get(color) ?? [];
      if (path.length > prev.length && path.length >= 2) {
        this.emitGearSound({ kind: 'extend', pathLength: path.length });
        break;
      }
      if (path.length < prev.length && prev.length >= 2) {
        this.emitGearSound({ kind: 'backtrack', pathLength: prev.length });
        break;
      }
    }

    this.snapshotPaths(state);

    if (this.segmentAnim && this.renderCallback && this.getAnimProgress() < 1) {
      this.scheduleRedraw();
    }
  }

  private snapshotPaths(state: GameState): void {
    this.prevPaths = new Map(
      [...state.paths.entries()].map(([color, path]) => [color, [...path]]),
    );
  }

  private getAnimProgress(): number {
    if (!this.segmentAnim) return 1;
    const t = (performance.now() - this.segmentAnim.startMs) / this.segmentAnim.durationMs;
    return Math.min(1, 1 - Math.pow(1 - t, 3));
  }

  private drawGearRails(state: GameState): void {
    const { ctx } = this;
    const n = state.size;
    const railW = this.cellSize * 0.07;
    const halfW = railW / 2;
    const railBase = this.themeColor('--gear-rail', '#2a2a34');
    const railHi = this.themeColor('--gear-rail-highlight', '#4a4a58');

    ctx.save();

    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n - 1; x++) {
        const p1 = this.posToPixel([x, y]);
        const p2 = this.posToPixel([x + 1, y]);
        const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
        grad.addColorStop(0, railBase);
        grad.addColorStop(0.5, railHi);
        grad.addColorStop(1, railBase);
        ctx.fillStyle = grad;
        ctx.fillRect(
          Math.min(p1.x, p2.x),
          p1.y - halfW,
          Math.abs(p2.x - p1.x),
          railW,
        );
      }
    }

    for (let y = 0; y < n - 1; y++) {
      for (let x = 0; x < n; x++) {
        const p1 = this.posToPixel([x, y]);
        const p2 = this.posToPixel([x, y + 1]);
        const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
        grad.addColorStop(0, railBase);
        grad.addColorStop(0.5, railHi);
        grad.addColorStop(1, railBase);
        ctx.fillStyle = grad;
        ctx.fillRect(
          p1.x - halfW,
          Math.min(p1.y, p2.y),
          railW,
          Math.abs(p2.y - p1.y),
        );
      }
    }

    ctx.restore();
  }

  private isEndpointAt(state: GameState, x: number, y: number): boolean {
    const pos: Pos = [x, y];
    for (const [, [a, b]] of state.endpoints) {
      if (posEqual(pos, a) || posEqual(pos, b)) return true;
    }
    return false;
  }

  private isWallAt(state: GameState, x: number, y: number): boolean {
    for (const [wx, wy] of state.walls) {
      if (wx === x && wy === y) return true;
    }
    return false;
  }

  private drawGearCellGears(state: GameState, activeColor: string | null): void {
    const { ctx } = this;
    const n = state.size;
    const outerR = this.cellSize * 0.45;
    const innerR = outerR * 0.65;
    const teeth = 12;
    const steelHi = this.themeColor('--gear-steel-highlight', '#8a8a98');
    const steelBase = this.themeColor('--gear-steel', '#6a6a78');

    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
    ctx.shadowBlur = this.cellSize * 0.1;
    ctx.shadowOffsetY = this.cellSize * 0.03;

    const wallInset = Math.max(3, this.cellSize * 0.1);
    const wallRadius = Math.max(4, this.cellSize * 0.18);

    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        if (this.isEndpointAt(state, x, y)) continue;

        if (this.isWallAt(state, x, y)) {
          this.drawWallCell(
            this.padding + x * this.cellSize + wallInset,
            this.padding + y * this.cellSize + wallInset,
            this.cellSize - wallInset * 2,
            wallRadius,
            isDarkTheme(),
          );
          continue;
        }

        const { x: cx, y: cy } = this.posToPixel([x, y]);
        const cellColor = state.grid[y]![x]!;
        const isOnPath = cellColor !== null;
        const isActive = isOnPath && cellColor === activeColor;

        let grad: CanvasGradient;
        let alpha: number;
        let glowColor: string | undefined;

        if (isOnPath) {
          const base = COLORS[cellColor] ?? '#888';
          const light = COLOR_LIGHT[cellColor] ?? base;
          grad = ctx.createRadialGradient(
            cx - outerR * 0.2,
            cy - outerR * 0.2,
            0,
            cx,
            cy,
            outerR,
          );
          grad.addColorStop(0, light);
          grad.addColorStop(0.6, base);
          grad.addColorStop(1, base);
          alpha = isActive ? 1 : 0.88;
          if (isActive) glowColor = base;
        } else {
          grad = ctx.createRadialGradient(
            cx - outerR * 0.2,
            cy - outerR * 0.2,
            0,
            cx,
            cy,
            outerR,
          );
          grad.addColorStop(0, steelHi);
          grad.addColorStop(0.55, steelBase);
          grad.addColorStop(1, steelBase);
          alpha = 0.82;
        }

        this.drawProceduralGear(
          cx,
          cy,
          outerR,
          innerR,
          teeth,
          grad,
          alpha,
          glowColor,
          this.computeGearRotation(state, x, y),
        );
      }
    }

    ctx.restore();
  }

  /**
   * Traces a mechanical gear outline into the current path: shallow,
   * flat-topped trapezoidal teeth around a solid disc body. Using a small
   * addendum (tip vs. root gap) and a higher tooth count makes the shape read
   * as a real gear instead of a pointy star.
   */
  private traceGearTeeth(
    cx: number,
    cy: number,
    tipR: number,
    rootR: number,
    teeth: number,
  ): void {
    const { ctx } = this;
    const pitch = (Math.PI * 2) / teeth;

    ctx.beginPath();
    for (let i = 0; i < teeth; i++) {
      const a = i * pitch;
      const profile: ReadonlyArray<readonly [number, number]> = [
        [a + pitch * 0.0, rootR],
        [a + pitch * 0.16, tipR],
        [a + pitch * 0.36, tipR],
        [a + pitch * 0.52, rootR],
      ];
      for (let k = 0; k < profile.length; k++) {
        const [angle, r] = profile[k]!;
        const px = cx + Math.cos(angle) * r;
        const py = cy + Math.sin(angle) * r;
        if (i === 0 && k === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
    }
    ctx.closePath();
  }

  private drawProceduralGear(
    cx: number,
    cy: number,
    outerR: number,
    innerR: number,
    teeth: number,
    fill: CanvasGradient | string,
    alpha: number,
    glowColor?: string,
    rotationRad = 0,
  ): void {
    const { ctx } = this;
    const rootR = outerR * 0.82;

    ctx.save();
    if (rotationRad !== 0) {
      ctx.translate(cx, cy);
      ctx.rotate(rotationRad);
      ctx.translate(-cx, -cy);
    }
    ctx.globalAlpha = alpha;

    if (glowColor) {
      ctx.shadowColor = glowColor;
      ctx.shadowBlur = this.cellSize * 0.35;
    }

    ctx.fillStyle = fill;
    this.traceGearTeeth(cx, cy, outerR, rootR, teeth);
    ctx.fill();

    ctx.shadowBlur = 0;

    // Rim groove just inside the teeth — adds machined depth.
    ctx.globalAlpha = alpha * 0.5;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.28)';
    ctx.lineWidth = Math.max(1, outerR * 0.045);
    ctx.beginPath();
    ctx.arc(cx, cy, rootR * 0.9, 0, Math.PI * 2);
    ctx.stroke();

    // Central hub.
    ctx.globalAlpha = alpha * 0.9;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
    ctx.beginPath();
    ctx.arc(cx, cy, innerR * 0.4, 0, Math.PI * 2);
    ctx.fill();

    // Bolt holes around the hub.
    ctx.globalAlpha = alpha * 0.85;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    const holeR = innerR * 0.13;
    for (let h = 0; h < 4; h++) {
      const angle = (h * Math.PI) / 2 + Math.PI / 4;
      const hx = cx + Math.cos(angle) * innerR * 0.6;
      const hy = cy + Math.sin(angle) * innerR * 0.6;
      ctx.beginPath();
      ctx.arc(hx, hy, holeR, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private drawEmptyCells(state: GameState): void {
    const { ctx } = this;
    const n = state.size;
    const inset = Math.max(3, this.cellSize * 0.1);
    const radius = Math.max(4, this.cellSize * 0.18);
    const dark = isDarkTheme();

    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const isWall = this.isWallAt(state, x, y);
        if (state.grid[y][x] && !isWall) continue;

        const px = this.padding + x * this.cellSize + inset;
        const py = this.padding + y * this.cellSize + inset;
        const size = this.cellSize - inset * 2;

        if (isWall) {
          this.drawWallCell(px, py, size, radius, dark);
          continue;
        }

        ctx.save();

        ctx.fillStyle = this.themeColor('--canvas-cell-empty', '#F0F0F5');
        this.roundRect(ctx, px, py, size, size, radius);
        ctx.fill();

        // Inset bevel: dark hairline along the top-left edge, light along
        // bottom-right — reads as a recessed socket rather than a flat tile.
        ctx.save();
        this.roundRect(ctx, px, py, size, size, radius);
        ctx.clip();
        ctx.strokeStyle = dark ? 'rgba(0, 0, 0, 0.45)' : 'rgba(0, 0, 0, 0.1)';
        ctx.lineWidth = Math.max(1, size * 0.06);
        ctx.beginPath();
        ctx.moveTo(px, py + size);
        ctx.lineTo(px, py);
        ctx.lineTo(px + size, py);
        ctx.stroke();
        ctx.strokeStyle = dark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.7)';
        ctx.beginPath();
        ctx.moveTo(px + size, py);
        ctx.lineTo(px + size, py + size);
        ctx.lineTo(px, py + size);
        ctx.stroke();
        ctx.restore();

        ctx.restore();
      }
    }
  }

  /** A blocked cell: a flat dark slab with a diagonal hatch — reads as "no entry" at a glance. */
  private drawWallCell(px: number, py: number, size: number, radius: number, dark: boolean): void {
    const { ctx } = this;

    ctx.save();
    this.roundRect(ctx, px, py, size, size, radius);
    ctx.fillStyle = dark ? 'rgba(0, 0, 0, 0.55)' : 'rgba(20, 20, 40, 0.28)';
    ctx.fill();

    ctx.save();
    this.roundRect(ctx, px, py, size, size, radius);
    ctx.clip();
    ctx.strokeStyle = dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.35)';
    ctx.lineWidth = Math.max(1, size * 0.08);
    const step = size * 0.28;
    for (let offset = -size; offset < size * 2; offset += step) {
      ctx.beginPath();
      ctx.moveTo(px + offset, py + size);
      ctx.lineTo(px + offset + size, py);
      ctx.stroke();
    }
    ctx.restore();

    ctx.restore();
  }

  private drawPaths(state: GameState, activeColor: string | null): void {
    for (const [color, path] of state.paths) {
      if (path.length === 0) continue;

      const isActive = color === activeColor;
      const base = COLORS[color] ?? '#888';
      const light = COLOR_LIGHT[color] ?? base;
      const endpoints = state.endpoints.get(color);
      const anim =
        this.segmentAnim?.color === color && this.getAnimProgress() < 1
          ? this.segmentAnim
          : null;
      const animProgress = anim ? this.getAnimProgress() : 1;
      const lastSegIndex = path.length - 2;
      const errorFx =
        this.errorAnim?.color === color ? this.getErrorVisuals(color) : null;

      for (let i = 0; i < path.length - 1; i++) {
        const [x1, y1] = path[i]!;
        const [x2, y2] = path[i + 1]!;
        if (anim?.type === 'extend' && i === lastSegIndex) continue;
        const isLastSeg = i === lastSegIndex;
        const segAlpha =
          errorFx && isLastSeg ? errorFx.chainFade : 1;
        this.drawStyledSegmentAnimated(
          x1,
          y1,
          x2,
          y2,
          base,
          light,
          isActive,
          i,
          1,
          segAlpha,
        );
        if (errorFx && isLastSeg && segAlpha > 0.05) {
          this.drawGearTornChainOverlay(x1, y1, x2, y2, base, segAlpha);
        }
      }

      if (anim?.type === 'extend') {
        this.drawStyledSegmentAnimated(
          anim.x1,
          anim.y1,
          anim.x2,
          anim.y2,
          base,
          light,
          isActive,
          lastSegIndex,
          animProgress,
          animProgress,
        );
      } else if (anim?.type === 'backtrack') {
        this.drawStyledSegmentAnimated(
          anim.x1,
          anim.y1,
          anim.x2,
          anim.y2,
          base,
          light,
          isActive,
          0,
          1 - animProgress,
          1 - animProgress,
        );
      }

      for (let i = 0; i < path.length; i++) {
        const pos = path[i]!;
        if (endpoints && (posEqual(pos, endpoints[0]) || posEqual(pos, endpoints[1]))) {
          continue;
        }
        this.drawStyledNode(pos[0], pos[1], base, light, isActive, i);
      }

      if (this.visualStyle !== 'gear' && endpoints && isPathComplete(path, endpoints)) {
        this.drawCompletedPathShimmer(path, base, light);
      }
    }
  }

  /** Continuous "energy flowing through the tube" overlay for a finished
   *  pair — a bright dashed stroke traced over the whole path with its dash
   *  offset animated by wall-clock time, so it visibly travels along the
   *  line forever (not a one-shot flash) as long as the pair stays
   *  connected. Added 2026-09-26 (user: "линия ... должн[а] двигаться после
   *  соединение") as the line-side counterpart to `drawDot`'s persistent
   *  `isPairComplete` pulse — gear is excluded, its own continuous
   *  chain-spin (`computeConnectedSpin`) already fills this role and a
   *  second overlay on top would just look cluttered. A plain overlay
   *  stroke works for every tube shape without touching any of the 30
   *  individual `drawXSegment` functions. */
  private drawCompletedPathShimmer(path: Pos[], base: string, light: string): void {
    if (!this.gearAnimationsEnabled() || path.length < 2) return;
    drawCompletedPathFx(
      this.ctx,
      this.visualStyle,
      path.map((p) => this.posToPixel(p)),
      this.cellSize,
      base,
      light,
      performance.now(),
      this.pipeRatio,
    );
    this.scheduleRedraw();
  }

  /** Wraps `drawStyledSegment` with a generic grow/dim effect for styles that
   *  don't natively understand `lengthProgress`/`alphaMul` — added 2026-09-25
   *  to generalize gear's path-draw and error-shake animations to every style
   *  without touching any of the 30 individual `drawXSegment` functions. Gear
   *  bypasses this (it already lerps its own geometry natively via those two
   *  params); everyone else gets a canvas clip that reveals the segment from
   *  its start point growing toward its end point, plus a plain alpha dim. */
  private drawStyledSegmentAnimated(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
    lengthProgress: number,
    alphaMul: number,
  ): void {
    if (this.visualStyle === 'gear' || (lengthProgress >= 1 && alphaMul >= 1)) {
      this.drawStyledSegment(x1, y1, x2, y2, base, light, isActive, segIndex, lengthProgress, alphaMul);
      return;
    }

    const { ctx } = this;
    ctx.save();
    if (alphaMul < 1) ctx.globalAlpha *= Math.max(0, alphaMul);

    if (lengthProgress < 1) {
      const p1 = this.posToPixel([x1, y1]);
      const p2 = this.posToPixel([x2, y2]);
      const isHorizontal = x1 !== x2;
      const pad = this.cellSize * 1.5;
      const progress = Math.max(0, Math.min(1, lengthProgress));

      let clipLeft: number;
      let clipTop: number;
      let clipWidth: number;
      let clipHeight: number;
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const fullWidth = Math.abs(p2.x - p1.x);
        const width = fullWidth * progress;
        clipLeft = p2.x >= p1.x ? left : left + fullWidth - width;
        clipTop = p1.y - pad;
        clipWidth = width;
        clipHeight = pad * 2;
      } else {
        const top = Math.min(p1.y, p2.y);
        const fullHeight = Math.abs(p2.y - p1.y);
        const height = fullHeight * progress;
        clipLeft = p1.x - pad;
        clipTop = p2.y >= p1.y ? top : top + fullHeight - height;
        clipWidth = pad * 2;
        clipHeight = height;
      }
      ctx.beginPath();
      ctx.moveTo(clipLeft, clipTop);
      ctx.lineTo(clipLeft + clipWidth, clipTop);
      ctx.lineTo(clipLeft + clipWidth, clipTop + clipHeight);
      ctx.lineTo(clipLeft, clipTop + clipHeight);
      ctx.closePath();
      ctx.clip();
    }

    this.drawStyledSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
    ctx.restore();
  }

  private drawStyledSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
    lengthProgress = 1,
    alphaMul = 1,
  ): void {
    switch (this.visualStyle) {
      case 'snake':
        this.drawSnakeSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'gear':
        this.drawGearSegment(
          x1,
          y1,
          x2,
          y2,
          base,
          light,
          isActive,
          segIndex,
          lengthProgress,
          alphaMul,
        );
        break;
      case 'chain':
        this.drawChainSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'rope':
        this.drawRopeSegment(x1, y1, x2, y2, base, light, isActive);
        break;
      case 'wave':
        this.drawWaveSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'crystal':
        this.drawCrystalSegment(x1, y1, x2, y2, base, light, isActive);
        break;
      case 'lightning':
        this.drawLightningSegment(x1, y1, x2, y2, base, light, isActive);
        break;
      case 'spring':
        this.drawSpringSegment(x1, y1, x2, y2, base, light, isActive);
        break;
      case 'thread':
        this.drawThreadSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'bubble':
        this.drawBubbleSegment(x1, y1, x2, y2, base, light, isActive);
        break;
      case 'ribbon':
        this.drawRibbonSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'fire':
        this.drawFireSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'vine':
        this.drawVineSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'magnet':
        this.drawMagnetSegment(x1, y1, x2, y2, base, light, isActive);
        break;
      case 'candy':
        this.drawCandySegment(x1, y1, x2, y2, base, light, isActive);
        break;
      case 'bone':
        this.drawPipeSegment(x1, y1, x2, y2, base, light, isActive);
        break;
      case 'magic':
        this.drawMagicSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'cyberpunk':
        this.drawCyberpunkSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'goth':
        this.drawGothSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'robot':
        this.drawRobotSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'wheel':
        this.drawWheelSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'emo':
        this.drawEmoSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'armor':
        this.drawArmorSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'chrome':
        this.drawChromeSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'viking':
        this.drawVikingSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'samurai':
        this.drawSamuraiSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'zombie':
        this.drawZombieSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'toxic':
        this.drawToxicSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      case 'demon':
        this.drawDemonSegment(x1, y1, x2, y2, base, light, isActive, segIndex);
        break;
      default:
        this.drawPipeSegment(x1, y1, x2, y2, base, light, isActive);
    }
  }

  private drawStyledNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
    nodeIndex: number,
  ): void {
    switch (this.visualStyle) {
      case 'snake':
        this.drawSnakeNode(x, y, base, light, isActive, nodeIndex);
        break;
      case 'gear':
        this.drawGearNode(x, y, base, light, isActive);
        break;
      case 'chain':
        this.drawChainNode(x, y, base, light, isActive, nodeIndex);
        break;
      case 'crystal':
        this.drawCrystalNode(x, y, base, light, isActive);
        break;
      case 'lightning':
        this.drawLightningNode(x, y, base, light, isActive);
        break;
      case 'bubble':
        this.drawBubbleNode(x, y, base, light, isActive);
        break;
      case 'fire':
        this.drawFireNode(x, y, base, light, isActive);
        break;
      case 'vine':
        this.drawVineNode(x, y, base, light, isActive);
        break;
      case 'magnet':
        this.drawMagnetNode(x, y, base, light, isActive);
        break;
      case 'candy':
        this.drawCandyNode(x, y, base, light, isActive);
        break;
      case 'bone':
        this.drawBoneNode(x, y, base, light, isActive);
        break;
      case 'magic':
        this.drawMagicNode(x, y, base, light, isActive);
        break;
      case 'cyberpunk':
        this.drawCyberpunkNode(x, y, base, light, isActive);
        break;
      case 'goth':
        this.drawGothNode(x, y, base, light, isActive);
        break;
      case 'robot':
        this.drawRobotNode(x, y, base, light, isActive);
        break;
      case 'wheel':
        this.drawWheelNode(x, y, base, light, isActive);
        break;
      case 'emo':
        this.drawEmoNode(x, y, base, light, isActive);
        break;
      case 'armor':
        this.drawArmorNode(x, y, base, light, isActive);
        break;
      case 'chrome':
        this.drawChromeNode(x, y, base, light, isActive);
        break;
      case 'viking':
        this.drawVikingNode(x, y, base, light, isActive);
        break;
      case 'samurai':
        this.drawSamuraiNode(x, y, base, light, isActive);
        break;
      case 'zombie':
        this.drawZombieNode(x, y, base, light, isActive);
        break;
      case 'toxic':
        this.drawToxicNode(x, y, base, light, isActive);
        break;
      case 'demon':
        this.drawDemonNode(x, y, base, light, isActive);
        break;
      default:
        this.drawPathNode(x, y, base, light, isActive);
    }
  }

  private drawPipeSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const half = (this.cellSize * this.pipeRatio) / 2;
    const isHorizontal = x1 !== x2;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;

    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * (0.22 + this.computeGearPulsePhase() * 0.1);
    }

    const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
    grad.addColorStop(0, light);
    grad.addColorStop(0.5, base);
    grad.addColorStop(1, light);
    ctx.fillStyle = grad;

    if (isHorizontal) {
      const left = Math.min(p1.x, p2.x);
      const width = Math.abs(p2.x - p1.x);
      this.roundRect(ctx, left, p1.y - half, width, half * 2, half);
    } else {
      const top = Math.min(p1.y, p2.y);
      const height = Math.abs(p2.y - p1.y);
      this.roundRect(ctx, p1.x - half, top, half * 2, height, half);
    }
    ctx.fill();
    ctx.shadowBlur = 0;

    // Cross-section shading, perpendicular to the tube's length — darker at
    // both edges, clear in the middle, so it reads as a round cylinder instead
    // of a flat painted bar even before the specular streak below.
    const crossGrad = isHorizontal
      ? ctx.createLinearGradient(0, p1.y - half, 0, p1.y + half)
      : ctx.createLinearGradient(p1.x - half, 0, p1.x + half, 0);
    crossGrad.addColorStop(0, 'rgba(0, 0, 0, 0.25)');
    crossGrad.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
    crossGrad.addColorStop(1, 'rgba(0, 0, 0, 0.25)');
    ctx.fillStyle = crossGrad;
    if (isHorizontal) {
      const left = Math.min(p1.x, p2.x);
      const width = Math.abs(p2.x - p1.x);
      this.roundRect(ctx, left, p1.y - half, width, half * 2, half);
    } else {
      const top = Math.min(p1.y, p2.y);
      const height = Math.abs(p2.y - p1.y);
      this.roundRect(ctx, p1.x - half, top, half * 2, height, half);
    }
    ctx.fill();

    // Glossy specular streak along the top edge of the tube — sells the
    // round cross-section instead of a flat painted bar.
    ctx.globalAlpha *= 0.55;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
    const sheenHalf = half * 0.32;
    if (isHorizontal) {
      const left = Math.min(p1.x, p2.x);
      const width = Math.abs(p2.x - p1.x);
      this.roundRect(ctx, left + half * 0.3, p1.y - half * 0.55, width - half * 0.6, sheenHalf, sheenHalf / 2);
    } else {
      const top = Math.min(p1.y, p2.y);
      const height = Math.abs(p2.y - p1.y);
      this.roundRect(ctx, p1.x - half * 0.55, top + half * 0.3, sheenHalf, height - half * 0.6, sheenHalf / 2);
    }
    ctx.fill();

    ctx.globalAlpha = 1;
    ctx.restore();
  }

  private drawSnakeSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    this.drawPipeSegment(x1, y1, x2, y2, base, light, isActive);

    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const scaleCount = Math.max(2, Math.floor(length / (this.cellSize * 0.35)));
    const scaleSize = this.cellSize * 0.12;

    ctx.save();
    ctx.globalAlpha = isActive ? 0.7 : 0.55;
    ctx.fillStyle = light;

    for (let s = 0; s < scaleCount; s++) {
      const t = (s + 0.5) / scaleCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const flip = (segIndex + s) % 2 === 0 ? 1 : -1;

      ctx.beginPath();
      if (isHorizontal) {
        ctx.ellipse(cx, cy + flip * scaleSize * 0.6, scaleSize, scaleSize * 0.55, 0, 0, Math.PI * 2);
      } else {
        ctx.ellipse(cx + flip * scaleSize * 0.6, cy, scaleSize * 0.55, scaleSize, 0, 0, Math.PI * 2);
      }
      ctx.fill();
    }

    ctx.restore();
  }

  private drawGearSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
    lengthProgress = 1,
    alphaMul = 1,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const fullP2 = this.posToPixel([x2, y2]);
    const p2 = {
      x: p1.x + (fullP2.x - p1.x) * lengthProgress,
      y: p1.y + (fullP2.y - p1.y) * lengthProgress,
    };
    const isHorizontal = x1 !== x2;
    const fullLength = isHorizontal
      ? Math.abs(fullP2.x - p1.x)
      : Math.abs(fullP2.y - p1.y);
    const length = fullLength * lengthProgress;
    if (length < 0.5) return;

    const linkCount = Math.max(
      1,
      Math.round(
        Math.max(3, Math.min(5, Math.round((fullLength / this.cellSize) * 4))) * lengthProgress,
      ),
    );
    const linkR = this.cellSize * 0.08;
    const segAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);

    ctx.save();
    ctx.globalAlpha = (isActive ? 1 : 0.88) * alphaMul;

    const shadowDepth = linkR * 1.8;
    const shadowGrad = isHorizontal
      ? ctx.createLinearGradient(p1.x, p1.y - shadowDepth, p1.x, p1.y + shadowDepth)
      : ctx.createLinearGradient(p1.x - shadowDepth, p1.y, p1.x + shadowDepth, p1.y);
    shadowGrad.addColorStop(0, 'rgba(0, 0, 0, 0.06)');
    shadowGrad.addColorStop(0.4, 'rgba(0, 0, 0, 0.24)');
    shadowGrad.addColorStop(0.6, 'rgba(0, 0, 0, 0.24)');
    shadowGrad.addColorStop(1, 'rgba(0, 0, 0, 0.06)');
    ctx.fillStyle = shadowGrad;
    if (isHorizontal) {
      ctx.fillRect(
        Math.min(p1.x, p2.x),
        p1.y - linkR * 1.7,
        length,
        linkR * 3.4,
      );
    } else {
      ctx.fillRect(
        p1.x - linkR * 1.7,
        Math.min(p1.y, p2.y),
        linkR * 3.4,
        length,
      );
    }

    const bloomMul = isActive ? 0.85 + this.computeGearPulsePhase() * 0.35 : 0.35;

    for (let l = 0; l < linkCount; l++) {
      const t = (l + 0.5) / linkCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const offset = (segIndex + l) % 2 === 0 ? 1 : -1;
      const perpOffset = this.cellSize * 0.022 * offset;
      const dropY = this.cellSize * 0.018;

      ctx.fillStyle = 'rgba(0, 0, 0, 0.16)';
      ctx.beginPath();
      if (isHorizontal) {
        ctx.ellipse(
          cx,
          cy + perpOffset + dropY,
          linkR * 1.2,
          linkR * 0.92,
          0,
          0,
          Math.PI * 2,
        );
      } else {
        ctx.ellipse(
          cx + perpOffset + dropY * 0.35,
          cy + dropY,
          linkR * 0.92,
          linkR * 1.2,
          0,
          0,
          Math.PI * 2,
        );
      }
      ctx.fill();

      if (isActive) {
        ctx.shadowColor = base;
        ctx.shadowBlur = this.cellSize * bloomMul * 0.22;
      }

      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, linkR * 1.6);
      grad.addColorStop(0, 'rgba(255, 255, 255, 0.25)');
      grad.addColorStop(0.35, light);
      grad.addColorStop(1, base);
      ctx.fillStyle = grad;

      ctx.beginPath();
      if (isHorizontal) {
        ctx.ellipse(cx, cy + perpOffset, linkR * 1.15, linkR, 0, 0, Math.PI * 2);
      } else {
        ctx.ellipse(cx + perpOffset, cy, linkR, linkR * 1.15, 0, 0, Math.PI * 2);
      }
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    this.drawGearChainWrap(p1.x, p1.y, base, segAngle + Math.PI, isActive, alphaMul);
    if (lengthProgress >= 0.95) {
      this.drawGearChainWrap(p2.x, p2.y, base, segAngle, isActive, alphaMul);
    }

    ctx.restore();
  }

  private drawGearChainWrap(
    cx: number,
    cy: number,
    base: string,
    tangentAngle: number,
    isActive: boolean,
    alphaMul = 1,
  ): void {
    const { ctx } = this;
    const wrapR = this.cellSize * 0.38;
    const thickness = this.cellSize * 0.05;
    const span = Math.PI * 0.83;

    ctx.save();
    ctx.globalAlpha = (isActive ? 0.95 : 0.82) * alphaMul;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * (0.18 + this.computeGearPulsePhase() * 0.12);
    }
    ctx.strokeStyle = base;
    ctx.lineWidth = thickness;
    ctx.lineCap = 'round';

    const midAngle = tangentAngle - Math.PI / 2;
    const startAngle = midAngle - span / 2;
    const endAngle = midAngle + span / 2;

    ctx.beginPath();
    ctx.arc(cx, cy, wrapR, startAngle, endAngle);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.lineWidth = thickness * 0.45;
    ctx.beginPath();
    ctx.arc(cx, cy, wrapR - thickness * 0.25, startAngle, endAngle);
    ctx.stroke();

    ctx.restore();
  }

  private drawChainSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const majorR = this.cellSize * 0.29;
    const minorR = this.cellSize * 0.14;
    const wireW = Math.max(2, this.cellSize * 0.08);
    // Overlap links so alternating rings visibly thread through each other.
    const spacing = majorR * 0.95;
    const linkCount = Math.max(2, Math.round(length / spacing));

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;

    for (let l = 0; l < linkCount; l++) {
      const t = (l + 0.5) / linkCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      // Real chain links alternate 90° so each ring passes through the next.
      const alongTravel = (segIndex + l) % 2 === 0;
      let rx: number;
      let ry: number;
      if (isHorizontal) {
        rx = alongTravel ? majorR : minorR;
        ry = alongTravel ? minorR : majorR;
      } else {
        rx = alongTravel ? minorR : majorR;
        ry = alongTravel ? majorR : minorR;
      }
      this.drawChainRing(cx, cy, rx, ry, wireW, base, light, isActive);
    }

    ctx.restore();
  }

  /** A hollow metallic ring — the basic building block of a real chain link. */
  private drawChainRing(
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    wireW: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;

    ctx.save();

    // Drop shadow so the ring reads as a raised loop of wire, not a flat line.
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.22)';
    ctx.lineWidth = wireW * 1.15;
    ctx.beginPath();
    ctx.ellipse(cx, cy + wireW * 0.12, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();

    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * (0.2 + this.computeGearPulsePhase() * 0.1);
    }

    // Base wire body.
    ctx.strokeStyle = base;
    ctx.lineWidth = wireW;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();

    ctx.shadowBlur = 0;

    // Diagonal sheen simulating a round metal cross-section.
    const grad = ctx.createLinearGradient(cx - rx, cy - ry, cx + rx, cy + ry);
    grad.addColorStop(0, light);
    grad.addColorStop(0.5, base);
    grad.addColorStop(1, light);
    ctx.strokeStyle = grad;
    ctx.lineWidth = wireW * 0.5;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();

    if (isActive) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.lineWidth = wireW * 0.22;
      ctx.beginPath();
      ctx.ellipse(cx - rx * 0.1, cy - ry * 0.1, rx * 0.72, ry * 0.72, 0, Math.PI * 1.1, Math.PI * 1.9);
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawRopeSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    this.drawPipeSegment(x1, y1, x2, y2, base, light, isActive);

    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2 * 0.55;

    ctx.save();
    ctx.globalAlpha = isActive ? 0.5 : 0.38;
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.04);
    ctx.setLineDash([this.cellSize * 0.08, this.cellSize * 0.06]);

    ctx.beginPath();
    if (isHorizontal) {
      ctx.moveTo(Math.min(p1.x, p2.x), p1.y - half);
      ctx.lineTo(Math.max(p1.x, p2.x), p1.y - half);
      ctx.moveTo(Math.min(p1.x, p2.x), p1.y + half);
      ctx.lineTo(Math.max(p1.x, p2.x), p1.y + half);
    } else {
      ctx.moveTo(p1.x - half, Math.min(p1.y, p2.y));
      ctx.lineTo(p1.x - half, Math.max(p1.y, p2.y));
      ctx.moveTo(p1.x + half, Math.min(p1.y, p2.y));
      ctx.lineTo(p1.x + half, Math.max(p1.y, p2.y));
    }
    ctx.stroke();
    ctx.restore();
  }

  private drawWaveSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const steps = Math.max(8, Math.floor(length / 4));
    const amplitude = this.cellSize * 0.07;
    const half = (this.cellSize * this.pipeRatio) / 2;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * (0.22 + this.computeGearPulsePhase() * 0.1);
    }

    const buildWavePath = () => {
      ctx.beginPath();
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        const cx = p1.x + (p2.x - p1.x) * t;
        const cy = p1.y + (p2.y - p1.y) * t;
        const wave = Math.sin(t * Math.PI * 4 + segIndex) * amplitude;

        if (isHorizontal) {
          if (s === 0) ctx.moveTo(cx, cy - half + wave);
          else ctx.lineTo(cx, cy - half + wave);
        } else {
          if (s === 0) ctx.moveTo(cx - half + wave, cy);
          else ctx.lineTo(cx - half + wave, cy);
        }
      }
      for (let s = steps; s >= 0; s--) {
        const t = s / steps;
        const cx = p1.x + (p2.x - p1.x) * t;
        const cy = p1.y + (p2.y - p1.y) * t;
        const wave = Math.sin(t * Math.PI * 4 + segIndex) * amplitude;

        if (isHorizontal) {
          ctx.lineTo(cx, cy + half + wave);
        } else {
          ctx.lineTo(cx + half + wave, cy);
        }
      }
      ctx.closePath();
    };

    const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
    grad.addColorStop(0, light);
    grad.addColorStop(0.5, base);
    grad.addColorStop(1, light);
    ctx.fillStyle = grad;
    buildWavePath();
    ctx.fill();

    // Cross-section shadow, same trick as the pipe — darker at both edges of
    // the ribbon so it reads as a rounded strip riding the wave, not a flat
    // painted squiggle.
    const crossGrad = isHorizontal
      ? ctx.createLinearGradient(0, p1.y - half - amplitude, 0, p1.y + half + amplitude)
      : ctx.createLinearGradient(p1.x - half - amplitude, 0, p1.x + half + amplitude, 0);
    crossGrad.addColorStop(0, 'rgba(0, 0, 0, 0.22)');
    crossGrad.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
    crossGrad.addColorStop(1, 'rgba(0, 0, 0, 0.22)');
    ctx.fillStyle = crossGrad;
    buildWavePath();
    ctx.fill();

    ctx.restore();
  }

  private drawCrystalSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const facet = half * 0.35;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * (0.22 + this.computeGearPulsePhase() * 0.1);
    }

    const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
    grad.addColorStop(0, light);
    grad.addColorStop(0.5, base);
    grad.addColorStop(1, light);
    ctx.fillStyle = grad;

    ctx.beginPath();
    if (isHorizontal) {
      const left = Math.min(p1.x, p2.x);
      const right = Math.max(p1.x, p2.x);
      const midY = p1.y;
      ctx.moveTo(left, midY);
      ctx.lineTo(left + facet, midY - half);
      ctx.lineTo(right - facet, midY - half);
      ctx.lineTo(right, midY);
      ctx.lineTo(right - facet, midY + half);
      ctx.lineTo(left + facet, midY + half);
    } else {
      const top = Math.min(p1.y, p2.y);
      const bottom = Math.max(p1.y, p2.y);
      const midX = p1.x;
      ctx.moveTo(midX, top);
      ctx.lineTo(midX - half, top + facet);
      ctx.lineTo(midX - half, bottom - facet);
      ctx.lineTo(midX, bottom);
      ctx.lineTo(midX + half, bottom - facet);
      ctx.lineTo(midX + half, top + facet);
    }
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Facet shading — split the gem in two along its length and shade each
    // half differently, so it reads as two angled faces catching light
    // unevenly instead of one smooth gradient-filled blob.
    ctx.beginPath();
    if (isHorizontal) {
      const left = Math.min(p1.x, p2.x);
      const right = Math.max(p1.x, p2.x);
      const midY = p1.y;
      ctx.moveTo(left, midY);
      ctx.lineTo(left + facet, midY - half);
      ctx.lineTo(right - facet, midY - half);
      ctx.lineTo(right, midY);
    } else {
      const top = Math.min(p1.y, p2.y);
      const bottom = Math.max(p1.y, p2.y);
      const midX = p1.x;
      ctx.moveTo(midX, top);
      ctx.lineTo(midX - half, top + facet);
      ctx.lineTo(midX - half, bottom - facet);
      ctx.lineTo(midX, bottom);
    }
    ctx.closePath();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.16)';
    ctx.fill();

    ctx.beginPath();
    if (isHorizontal) {
      const left = Math.min(p1.x, p2.x);
      const right = Math.max(p1.x, p2.x);
      const midY = p1.y;
      ctx.moveTo(left, midY);
      ctx.lineTo(left + facet, midY + half);
      ctx.lineTo(right - facet, midY + half);
      ctx.lineTo(right, midY);
    } else {
      const top = Math.min(p1.y, p2.y);
      const bottom = Math.max(p1.y, p2.y);
      const midX = p1.x;
      ctx.moveTo(midX, top);
      ctx.lineTo(midX + half, top + facet);
      ctx.lineTo(midX + half, bottom - facet);
      ctx.lineTo(midX, bottom);
    }
    ctx.closePath();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.16)';
    ctx.fill();

    ctx.restore();
  }

  /** Clean tube body (same one `pipe` uses, cross-section shading included)
   *  with a single crisp bright zigzag "electric core" streak down the middle
   *  — replaces the old design, which jagged the tube's own outline every
   *  ~0.35 cells AND stacked a bolt icon on top of that at every cell,
   *  reading as visual noise rather than electricity (reported by the user
   *  as "ужасная графика линий" on a Lightning-style level). */
  private drawLightningSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    this.drawPipeSegment(x1, y1, x2, y2, base, light, isActive);

    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const midX = (p1.x + p2.x) / 2;
    const midY = (p1.y + p2.y) / 2;
    const jag = half * 0.55;
    const flip = (Math.round(x1 + y1) % 2 === 0 ? 1 : -1) * (isHorizontal ? 1 : -1);

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.75;
    ctx.shadowColor = 'rgba(255, 255, 255, 0.9)';
    ctx.shadowBlur = this.cellSize * (isActive ? 0.3 : 0.12);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.045);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    if (isHorizontal) {
      ctx.lineTo(midX - half * 0.35, midY - flip * jag);
      ctx.lineTo(midX + half * 0.35, midY + flip * jag);
    } else {
      ctx.lineTo(midX - flip * jag, midY - half * 0.35);
      ctx.lineTo(midX + flip * jag, midY + half * 0.35);
    }
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();
    ctx.restore();
  }

  private drawPathNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const radius = (this.cellSize * this.pipeRatio) / 2;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;

    const grad = ctx.createRadialGradient(
      cx - radius * 0.25,
      cy - radius * 0.25,
      0,
      cx,
      cy,
      radius,
    );
    grad.addColorStop(0, light);
    grad.addColorStop(0.6, base);
    grad.addColorStop(1, base);

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();

    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.35;
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 0.85, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    ctx.globalAlpha = 1;
    ctx.restore();
  }

  private drawSnakeNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
    nodeIndex: number,
  ): void {
    this.drawPathNode(x, y, base, light, isActive);

    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = this.cellSize * 0.08;

    ctx.save();
    ctx.globalAlpha = 0.6;
    ctx.fillStyle = light;
    const angle = (nodeIndex * Math.PI) / 2;
    for (let i = 0; i < 4; i++) {
      const a = angle + (i * Math.PI) / 2;
      ctx.beginPath();
      ctx.ellipse(
        cx + Math.cos(a) * r * 1.8,
        cy + Math.sin(a) * r * 1.8,
        r,
        r * 0.6,
        a,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
    ctx.restore();
  }

  private drawGearNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = this.cellSize * 0.12;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.9;

    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * (0.15 + this.computeGearPulsePhase() * 0.12);
    }

    const grad = ctx.createRadialGradient(cx - r * 0.2, cy - r * 0.2, 0, cx, cy, r);
    grad.addColorStop(0, light);
    grad.addColorStop(0.6, base);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;

    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI) / 3 - Math.PI / 6;
      const px = cx + Math.cos(angle) * r;
      const py = cy + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();

    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.35, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.22)';
    ctx.lineWidth = Math.max(1, r * 0.14);
    ctx.beginPath();
    ctx.moveTo(cx - r * 0.42, cy);
    ctx.lineTo(cx + r * 0.42, cy);
    ctx.stroke();

    ctx.restore();
  }

  private drawChainNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
    nodeIndex: number,
  ): void {
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const majorR = this.cellSize * 0.29;
    const minorR = this.cellSize * 0.14;
    const wireW = Math.max(2, this.cellSize * 0.08);
    // A turn in the path is itself a ring — alternate it in step with the
    // adjoining segment rings so the whole chain keeps threading correctly.
    const rx = nodeIndex % 2 === 0 ? majorR : minorR;
    const ry = nodeIndex % 2 === 0 ? minorR : majorR;

    this.ctx.save();
    this.ctx.globalAlpha = isActive ? 1 : 0.88;
    this.drawChainRing(cx, cy, rx, ry, wireW, base, light, isActive);
    this.ctx.restore();
  }

  private drawCrystalNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = (this.cellSize * this.pipeRatio) / 2;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;

    const grad = ctx.createRadialGradient(cx - r * 0.2, cy - r * 0.2, 0, cx, cy, r);
    grad.addColorStop(0, light);
    grad.addColorStop(0.5, base);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;

    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI) / 3 - Math.PI / 6;
      const px = cx + Math.cos(angle) * r;
      const py = cy + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();
  }

  /** A small glowing spark at each path cell — kept deliberately quiet (just a
   *  bright dot, not a bolt icon) since the segment itself now carries the
   *  "electric" read via its zigzag core stroke; stacking a busy icon on top
   *  of that at every cell was the main source of the "messy" look. */
  private drawLightningNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = this.cellSize * 0.06;

    ctx.save();
    ctx.globalAlpha = isActive ? 0.95 : 0.55;
    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.28 : 0.1);
    ctx.strokeStyle = base;
    ctx.lineWidth = Math.max(1, r * 0.6);
    ctx.beginPath();
    ctx.arc(cx, cy, r * 1.6, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  /** A core wire with a shaded metal ellipse ("coil loop") stacked every
   *  ~0.16 cells — reads as an actual slinky/spring in profile, unlike the
   *  old design (a bare zigzag stroke, flagged by the user as "too simple
   *  and childish" alongside thread/magnet/magic). Echoes the spring dot's
   *  concentric-ring motif so segment and endpoint read as the same object. */
  private drawSpringSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const half = (this.cellSize * this.pipeRatio) / 2;
    const loopCount = Math.max(4, Math.round(length / (this.cellSize * 0.16)));

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * (0.2 + this.computeGearPulsePhase() * 0.1);
    }

    ctx.strokeStyle = base;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.035);
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();
    ctx.shadowBlur = 0;

    for (let i = 0; i <= loopCount; i++) {
      const t = i / loopCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const rx = isHorizontal ? half * 0.3 : half * 0.85;
      const ry = isHorizontal ? half * 0.85 : half * 0.3;

      const grad = ctx.createLinearGradient(cx - rx, cy - ry, cx + rx, cy + ry);
      grad.addColorStop(0, light);
      grad.addColorStop(0.5, base);
      grad.addColorStop(1, light);
      ctx.strokeStyle = grad;
      ctx.lineWidth = Math.max(2, this.cellSize * 0.075);
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawThreadSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const steps = Math.max(14, Math.floor(length / 4));
    const amplitude = this.cellSize * 0.075;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.18;
    }
    ctx.lineCap = 'round';

    // Two strands twisting around each other (opposite phase) instead of one
    // squiggle with ticks stuck on — reads as plied thread/yarn, not a
    // marker-drawn line.
    const buildStrand = (phase: number) => {
      ctx.beginPath();
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        const cx = p1.x + (p2.x - p1.x) * t;
        const cy = p1.y + (p2.y - p1.y) * t;
        const wobble = Math.sin(t * Math.PI * 5 + segIndex + phase) * amplitude;
        const px = isHorizontal ? cx : cx + wobble;
        const py = isHorizontal ? cy + wobble : cy;
        if (s === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
    };

    ctx.strokeStyle = base;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.055);
    buildStrand(0);
    ctx.stroke();

    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.055);
    buildStrand(Math.PI);
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.globalAlpha *= 0.55;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.lineWidth = Math.max(1, this.cellSize * 0.02);
    buildStrand(0);
    ctx.stroke();

    ctx.restore();
  }

  private drawBubbleSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const bubbleR = this.cellSize * 0.13;
    const length = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const count = Math.max(3, Math.round(length / (bubbleR * 1.6)));

    ctx.save();
    for (let i = 0; i < count; i++) {
      const t = (i + 0.5) / count;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const r = bubbleR * (0.72 + 0.28 * Math.abs(Math.sin(i * 1.7)));

      ctx.globalAlpha = isActive ? 0.85 : 0.68;
      const grad = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, 0, cx, cy, r);
      grad.addColorStop(0, light);
      grad.addColorStop(0.7, base);
      grad.addColorStop(1, base);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalAlpha *= 0.7;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.beginPath();
      ctx.arc(cx - r * 0.35, cy - r * 0.35, r * 0.25, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawBubbleNode(x: number, y: number, base: string, light: string, isActive: boolean): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 1.05;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.3;
    }
    const grad = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, 0, cx, cy, r);
    grad.addColorStop(0, light);
    grad.addColorStop(0.7, base);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.globalAlpha *= 0.8;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.lineWidth = Math.max(1, r * 0.12);
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.68, 0.15 * Math.PI, 1.05 * Math.PI);
    ctx.stroke();
    ctx.restore();
  }

  private drawRibbonSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const steps = Math.max(10, Math.floor(length / 4));
    const half = (this.cellSize * this.pipeRatio) / 2;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * (0.2 + this.computeGearPulsePhase() * 0.1);
    }

    const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
    grad.addColorStop(0, light);
    grad.addColorStop(0.5, base);
    grad.addColorStop(1, light);
    ctx.fillStyle = grad;

    const widthAt = (t: number) => half * (0.4 + 0.6 * Math.abs(Math.cos(t * Math.PI * 2 + segIndex)));

    ctx.beginPath();
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const w = widthAt(t);
      if (isHorizontal) {
        if (s === 0) ctx.moveTo(cx, cy - w);
        else ctx.lineTo(cx, cy - w);
      } else {
        if (s === 0) ctx.moveTo(cx - w, cy);
        else ctx.lineTo(cx - w, cy);
      }
    }
    for (let s = steps; s >= 0; s--) {
      const t = s / steps;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const w = widthAt(t);
      if (isHorizontal) ctx.lineTo(cx, cy + w);
      else ctx.lineTo(cx + w, cy);
    }
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  }

  private drawFireSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    this.drawPipeSegment(x1, y1, x2, y2, base, light, isActive);

    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const flameCount = Math.max(2, Math.floor(length / (this.cellSize * 0.3)));
    const half = (this.cellSize * this.pipeRatio) / 2;

    ctx.save();
    ctx.globalAlpha = isActive ? 0.85 : 0.65;
    ctx.fillStyle = light;
    for (let f = 0; f < flameCount; f++) {
      const t = (f + 0.5) / flameCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const flip = (f + segIndex) % 2 === 0 ? 1 : -1;
      const flameH = half * (1 + 0.4 * Math.sin(f * 2.1));

      ctx.beginPath();
      if (isHorizontal) {
        ctx.moveTo(cx - half * 0.45, cy + flip * half * 0.6);
        ctx.quadraticCurveTo(cx, cy + flip * (half * 0.6 + flameH), cx + half * 0.45, cy + flip * half * 0.6);
      } else {
        ctx.moveTo(cx + flip * half * 0.6, cy - half * 0.45);
        ctx.quadraticCurveTo(cx + flip * (half * 0.6 + flameH), cy, cx + flip * half * 0.6, cy + half * 0.45);
      }
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  private drawFireNode(x: number, y: number, base: string, light: string, isActive: boolean): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = (this.cellSize * this.pipeRatio) / 2;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.35;
    }
    const grad = ctx.createRadialGradient(cx, cy + r * 0.3, 0, cx, cy, r * 1.15);
    grad.addColorStop(0, light);
    grad.addColorStop(0.55, base);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;

    ctx.beginPath();
    ctx.moveTo(cx, cy - r * 1.3);
    ctx.quadraticCurveTo(cx + r * 0.9, cy - r * 0.4, cx + r * 0.7, cy + r * 0.35);
    ctx.arc(cx, cy + r * 0.15, r * 0.75, 0.2 * Math.PI, 0.8 * Math.PI);
    ctx.quadraticCurveTo(cx - r * 0.9, cy - r * 0.4, cx, cy - r * 1.3);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  private drawVineSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    this.drawWaveSegment(x1, y1, x2, y2, base, light, isActive, segIndex);

    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const leafCount = Math.max(1, Math.floor(length / (this.cellSize * 0.5)));
    const half = (this.cellSize * this.pipeRatio) / 2;

    ctx.save();
    ctx.globalAlpha = isActive ? 0.8 : 0.6;
    ctx.fillStyle = light;
    for (let l = 0; l < leafCount; l++) {
      const t = (l + 0.5) / leafCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const flip = (l + segIndex) % 2 === 0 ? 1 : -1;
      const leafSize = half * 0.9;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(isHorizontal ? flip * 0.6 : Math.PI / 2 + flip * 0.6);
      ctx.beginPath();
      ctx.ellipse(leafSize * 0.8, 0, leafSize, leafSize * 0.45, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    ctx.restore();
  }

  private drawVineNode(x: number, y: number, base: string, light: string, isActive: boolean): void {
    this.drawPathNode(x, y, base, light, isActive);

    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = this.cellSize * 0.06;

    ctx.save();
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = light;
    for (let i = 0; i < 5; i++) {
      const a = (i * Math.PI * 2) / 5;
      ctx.beginPath();
      ctx.ellipse(cx + Math.cos(a) * r * 1.3, cy + Math.sin(a) * r * 1.3, r, r * 0.55, a, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = 'rgba(255, 220, 80, 0.9)';
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.7, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /** Curved field-line arcs bowing off the tube (like iron filings around a
   *  bar magnet) instead of flat perpendicular tally marks — the old accent
   *  read as generic tick marks, not magnetism (flagged by the user as
   *  "too simple and childish" alongside spring/thread/magic). */
  private drawMagnetSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    this.drawPipeSegment(x1, y1, x2, y2, base, light, isActive);

    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;

    ctx.save();
    ctx.globalAlpha = isActive ? 0.85 : 0.55;
    if (isActive) {
      ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
      ctx.shadowBlur = this.cellSize * 0.12;
    }
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = Math.max(1.2, this.cellSize * 0.025);
    ctx.lineCap = 'round';

    const arcCount = 3;
    for (let i = 0; i < arcCount; i++) {
      const t = (i + 0.5) / arcCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;

      for (const side of [-1, 1]) {
        ctx.beginPath();
        if (isHorizontal) {
          ctx.moveTo(cx - half * 0.55, cy + side * half * 0.6);
          ctx.quadraticCurveTo(cx, cy + side * half * 1.35, cx + half * 0.55, cy + side * half * 0.6);
        } else {
          ctx.moveTo(cx + side * half * 0.6, cy - half * 0.55);
          ctx.quadraticCurveTo(cx + side * half * 1.35, cy, cx + side * half * 0.6, cy + half * 0.55);
        }
        ctx.stroke();
      }
    }
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  private drawMagnetNode(x: number, y: number, base: string, light: string, isActive: boolean): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 1.05;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.3;
    }

    ctx.fillStyle = '#DC2626';
    ctx.beginPath();
    ctx.arc(cx, cy, r, -Math.PI / 2, Math.PI / 2);
    ctx.closePath();
    ctx.fill();

    const grad = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, 0, cx, cy, r);
    grad.addColorStop(0, light);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, Math.PI / 2, Math.PI * 1.5);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.lineWidth = Math.max(1, r * 0.1);
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx, cy + r);
    ctx.stroke();
    ctx.restore();
  }

  private drawCandySegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const half = (this.cellSize * this.pipeRatio) / 2;
    const isHorizontal = x1 !== x2;

    const pillPath = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;
    ctx.fillStyle = light;
    pillPath();
    ctx.fill();

    ctx.save();
    pillPath();
    ctx.clip();
    ctx.fillStyle = base;
    const stripeW = half * 0.9;
    const span = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const originX = Math.min(p1.x, p2.x);
    const originY = Math.min(p1.y, p2.y);
    for (let along = -half * 2; along < span + half * 2; along += stripeW * 2) {
      ctx.beginPath();
      if (isHorizontal) {
        ctx.moveTo(originX + along, p1.y - half * 1.5);
        ctx.lineTo(originX + along + stripeW, p1.y - half * 1.5);
        ctx.lineTo(originX + along + stripeW + half * 2, p1.y + half * 1.5);
        ctx.lineTo(originX + along + half * 2, p1.y + half * 1.5);
      } else {
        ctx.moveTo(p1.x - half * 1.5, originY + along);
        ctx.lineTo(p1.x - half * 1.5, originY + along + stripeW);
        ctx.lineTo(p1.x + half * 1.5, originY + along + stripeW + half * 2);
        ctx.lineTo(p1.x + half * 1.5, originY + along + half * 2);
      }
      ctx.closePath();
      ctx.fill();
    }

    // Cross-section shadow on top of the stripes — same trick as the pipe —
    // so the candy cane reads as a round-cross-section stick, not a flat
    // striped decal.
    const crossGrad = isHorizontal
      ? ctx.createLinearGradient(0, p1.y - half, 0, p1.y + half)
      : ctx.createLinearGradient(p1.x - half, 0, p1.x + half, 0);
    crossGrad.addColorStop(0, 'rgba(0, 0, 0, 0.2)');
    crossGrad.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
    crossGrad.addColorStop(1, 'rgba(0, 0, 0, 0.2)');
    ctx.fillStyle = crossGrad;
    pillPath();
    ctx.fill();

    ctx.restore();
    ctx.restore();
  }

  private drawCandyNode(x: number, y: number, base: string, light: string, isActive: boolean): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 1.05;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.3;
    }
    ctx.fillStyle = light;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.clip();
    ctx.strokeStyle = base;
    ctx.lineWidth = r * 0.35;
    ctx.beginPath();
    for (let a = 0; a < Math.PI * 4; a += 0.2) {
      const rad = (a / (Math.PI * 4)) * r * 1.3;
      const px = cx + Math.cos(a) * rad;
      const py = cy + Math.sin(a) * rad;
      if (a === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
    ctx.restore();
    ctx.restore();
  }

  private drawBoneNode(x: number, y: number, base: string, light: string, isActive: boolean): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = this.cellSize * 0.14;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.25;
    }
    const grad = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, 0, cx, cy, r);
    grad.addColorStop(0, light);
    grad.addColorStop(0.6, base);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;

    ctx.beginPath();
    ctx.arc(cx, cy - r * 0.55, r * 0.62, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy + r * 0.55, r * 0.62, 0, Math.PI * 2);
    ctx.fill();
    this.roundRect(ctx, cx - r * 0.32, cy - r * 0.55, r * 0.64, r * 1.1, r * 0.3);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  private drawSparklePath(cx: number, cy: number, r: number): void {
    const { ctx } = this;
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx + r * 0.25, cy - r * 0.25);
    ctx.lineTo(cx + r, cy);
    ctx.lineTo(cx + r * 0.25, cy + r * 0.25);
    ctx.lineTo(cx, cy + r);
    ctx.lineTo(cx - r * 0.25, cy + r * 0.25);
    ctx.lineTo(cx - r, cy);
    ctx.lineTo(cx - r * 0.25, cy - r * 0.25);
    ctx.closePath();
  }

  private drawMagicSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    this.drawPipeSegment(x1, y1, x2, y2, base, light, isActive);

    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const sparkleCount = Math.max(2, Math.floor(length / (this.cellSize * 0.28)));

    ctx.save();
    ctx.globalAlpha = isActive ? 0.95 : 0.65;
    ctx.shadowColor = base;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.22 : 0.1);
    for (let i = 0; i < sparkleCount; i++) {
      const t = (i + 0.5) / sparkleCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const flip = (i + segIndex) % 2 === 0 ? 1 : -1;
      const off = this.cellSize * 0.12;
      const ox = isHorizontal ? 0 : flip * off;
      const oy = isHorizontal ? flip * off : 0;
      const sr = this.cellSize * 0.065;

      const grad = ctx.createRadialGradient(cx + ox, cy + oy, 0, cx + ox, cy + oy, sr);
      grad.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
      grad.addColorStop(0.6, light);
      grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = grad;
      this.drawSparklePath(cx + ox, cy + oy, sr);
      ctx.fill();
    }
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  private drawMagicNode(x: number, y: number, base: string, light: string, isActive: boolean): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = (this.cellSize * this.pipeRatio) / 2;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.88;
    ctx.shadowColor = base;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.4 : 0.2);

    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 1.2);
    grad.addColorStop(0, light);
    grad.addColorStop(0.6, base);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    this.drawSparklePath(cx, cy, r * 1.15);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
    this.drawSparklePath(cx, cy, r * 0.5);
    ctx.fill();
    ctx.restore();
  }

  private drawDot(pos: Pos, color: string, isActive: boolean, state: GameState): void {
    const { ctx } = this;
    const { x, y } = this.posToPixel(pos);
    const radius = this.cellSize * this.dotScale;
    const base = COLORS[color] ?? '#888';
    const light = COLOR_LIGHT[color] ?? base;

    ctx.save();

    if (this.visualStyle === 'gear') {
      this.drawGearDot(x, y, radius, base, light, isActive, color, pos, state);
      ctx.restore();
      return;
    }

    // Generic pulse/flash/error-shake — generalized 2026-09-25 from gear's
    // endpoint feedback (`drawGearDot` above) so every style's dot gets the
    // same "alive" cues without a per-style rewrite: `computeGearPulseScale`/
    // `getEndpointFlashMul`/`getErrorVisuals` were never gear-specific in their
    // own math, only in who called them. Flash is folded into scale (rather
    // than a glow) since a wrapper-level shadowBlur would just get overwritten
    // by whatever each style's own draw function sets internally. `snake` used
    // to bypass this block entirely (its own early-return, same shape as
    // gear's) — missed in the original generalization pass, so its dots got
    // no animation at all; fixed 2026-09-26 (user screenshot: "где тут
    // анимация?" on a snake-styled completed pair) by folding it into this
    // shared path instead of giving it a third special case.
    //
    // `isPairComplete` keeps the pulse running for EVERY finished pair, not
    // just the one currently being dragged (`isActive`) — matches gear's own
    // `connectedCells`-driven chain-spin, which never turns off once a pair
    // is connected. Without this, a pair's dots went fully static the moment
    // you moved on to the next color, which read as "no animation at all"
    // once you stopped actively dragging it (user: "точки должны двигаться
    // после соединение", 2026-09-26).
    const endpoints = state.endpoints.get(color);
    const isPairComplete = endpoints ? isPathComplete(state.paths.get(color), endpoints) : false;
    const pulse = isActive || isPairComplete ? this.computeGearPulseScale(isPairComplete ? 0.09 : 0.06) : 1;
    const flashMul = this.getEndpointFlashMul(color);
    const errorFx = this.getErrorVisuals(color);
    const scale = pulse * flashMul;

    if (errorFx) {
      ctx.translate(errorFx.dx, errorFx.dy);
      ctx.globalAlpha *= errorFx.dim;
    }
    if (scale !== 1) {
      ctx.translate(x, y);
      ctx.scale(scale, scale);
      ctx.translate(-x, -y);
    }

    if (isPairComplete && this.gearAnimationsEnabled()) {
      drawCompletedDotFx(ctx, this.visualStyle, x, y, radius, light, performance.now(), pos[0] * 0.37 + pos[1] * 0.53);
    }

    if (this.visualStyle === 'snake') {
      this.drawSnakeDot(x, y, radius, base, light, isActive);
    } else {
      this.drawStyledDot(x, y, radius, base, light, isActive);
    }
    ctx.restore();
  }

  /** Endpoint dots, one silhouette per visual style so a level doesn't show the
   *  same plain circle 18 times over — each still ends in `paintOrbBase()` or
   *  its own glow so it reads as "this is a draggable endpoint", just shaped
   *  to match that style's line art (facets for crystal, a horseshoe for
   *  magnet, a bone for bone, ...). `snake`/`gear` have their own dots already
   *  (see `drawDot`) and aren't routed through here. */
  private drawStyledDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    switch (this.visualStyle) {
      case 'crystal':
        this.drawCrystalDot(x, y, radius, base, light, isActive);
        return;
      case 'lightning':
        this.drawLightningDot(x, y, radius, base, light, isActive);
        return;
      case 'chain':
        this.drawChainDot(x, y, radius, base, light, isActive);
        return;
      case 'rope':
        this.drawRopeDot(x, y, radius, base, light, isActive);
        return;
      case 'wave':
        this.drawWaveDot(x, y, radius, base, light, isActive);
        return;
      case 'spring':
        this.drawSpringDot(x, y, radius, base, light, isActive);
        return;
      case 'thread':
        this.drawThreadDot(x, y, radius, base, light, isActive);
        return;
      case 'bubble':
        this.drawBubbleDot(x, y, radius, base, light, isActive);
        return;
      case 'ribbon':
        this.drawRibbonDot(x, y, radius, base, light, isActive);
        return;
      case 'fire':
        this.drawFireDot(x, y, radius, base, light, isActive);
        return;
      case 'vine':
        this.drawVineDot(x, y, radius, base, light, isActive);
        return;
      case 'magnet':
        this.drawMagnetDot(x, y, radius, base, light, isActive);
        return;
      case 'candy':
        this.drawCandyDot(x, y, radius, base, light, isActive);
        return;
      case 'bone':
        this.drawBoneDot(x, y, radius, base, light, isActive);
        return;
      case 'magic':
        this.drawMagicDot(x, y, radius, base, light, isActive);
        return;
      case 'cyberpunk':
        this.drawCyberpunkDot(x, y, radius, base, light, isActive);
        return;
      case 'goth':
        this.drawGothDot(x, y, radius, base, light, isActive);
        return;
      case 'robot':
        this.drawRobotDot(x, y, radius, base, light, isActive);
        return;
      case 'wheel':
        this.drawWheelDot(x, y, radius, base, light, isActive);
        return;
      case 'emo':
        this.drawEmoDot(x, y, radius, base, light, isActive);
        return;
      case 'armor':
        this.drawArmorDot(x, y, radius, base, light, isActive);
        return;
      case 'chrome':
        this.drawChromeDot(x, y, radius, base, light, isActive);
        return;
      case 'viking':
        this.drawVikingDot(x, y, radius, base, light, isActive);
        return;
      case 'samurai':
        this.drawSamuraiDot(x, y, radius, base, light, isActive);
        return;
      case 'zombie':
        this.drawZombieDot(x, y, radius, base, light, isActive);
        return;
      case 'toxic':
        this.drawToxicDot(x, y, radius, base, light, isActive);
        return;
      case 'demon':
        this.drawDemonDot(x, y, radius, base, light, isActive);
        return;
      default:
        this.drawSphereDot(x, y, radius, base, light, isActive);
    }
  }

  /** Base glass-sphere fill: gradient body + darkened rim (sells curvature) +
   *  a crisp specular spot. Shared by `pipe`'s dot and as the base every other
   *  orb-shaped dot (wave/rope/bubble/fire/vine/candy/magic) builds its accent
   *  on top of, so the "material" reads consistent even as the topper differs. */
  private paintOrbBase(x: number, y: number, radius: number, base: string, light: string): void {
    const { ctx } = this;
    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(20, 20, 40, 0.18)');
    ctx.shadowBlur = this.cellSize * 0.16;
    ctx.shadowOffsetY = this.cellSize * 0.05;

    const grad = ctx.createRadialGradient(
      x - radius * 0.3,
      y - radius * 0.35,
      radius * 0.05,
      x,
      y,
      radius,
    );
    grad.addColorStop(0, this.themeColor('--canvas-dot-highlight', '#FFFFFF'));
    grad.addColorStop(0.22, light);
    grad.addColorStop(0.62, base);
    grad.addColorStop(1, base);

    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();

    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    const rimGrad = ctx.createRadialGradient(x, y, radius * 0.6, x, y, radius);
    rimGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
    rimGrad.addColorStop(1, 'rgba(0, 0, 0, 0.22)');
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = rimGrad;
    ctx.fill();
  }

  private paintOrbSpecular(x: number, y: number, radius: number): void {
    const { ctx } = this;
    ctx.beginPath();
    ctx.ellipse(
      x - radius * 0.32,
      y - radius * 0.38,
      radius * 0.26,
      radius * 0.16,
      -0.5,
      0,
      Math.PI * 2,
    );
    const specGrad = ctx.createRadialGradient(
      x - radius * 0.32,
      y - radius * 0.38,
      0,
      x - radius * 0.32,
      y - radius * 0.38,
      radius * 0.26,
    );
    specGrad.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
    specGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = specGrad;
    ctx.fill();
  }

  /** White ring (marks "draggable endpoint", every dot needs one to stay
   *  legible against a mid-path bead) + active-state glow. Only used by dots
   *  whose silhouette is a plain circle — shapes with their own outline
   *  (crystal's hex, magnet's horseshoe, bone, ribbon, ...) skip it and glow
   *  their own outline instead so the ring doesn't float disconnected from
   *  a non-circular body. */
  private paintDotRing(x: number, y: number, radius: number, base: string, isActive: boolean): void {
    const { ctx } = this;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = Math.max(2.5, this.cellSize * 0.05);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.stroke();

    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.4;
      ctx.beginPath();
      ctx.arc(x, y, radius + 2, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }

  private drawSphereDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    this.paintOrbBase(x, y, radius, base, light);
    this.paintOrbSpecular(x, y, radius);
    this.paintDotRing(x, y, radius, base, isActive);
  }

  /** Faceted gem — matches `drawCrystalSegment`'s hex-with-split-shading look
   *  instead of the round default. No separate ring: the hex's own edge is
   *  the outline, and it glows on its own stroke when active. */
  private drawCrystalDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const r = radius * 1.05;

    const hexPath = () => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 2;
        const px = x + Math.cos(angle) * r;
        const py = y + Math.sin(angle) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
    };

    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(20, 20, 40, 0.18)');
    ctx.shadowBlur = this.cellSize * 0.14;
    ctx.shadowOffsetY = this.cellSize * 0.04;
    const grad = ctx.createLinearGradient(x, y - r, x, y + r);
    grad.addColorStop(0, light);
    grad.addColorStop(0.5, base);
    grad.addColorStop(1, base);
    hexPath();
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    ctx.save();
    hexPath();
    ctx.clip();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.22)';
    ctx.beginPath();
    ctx.moveTo(x - r, y - r);
    ctx.lineTo(x + r, y - r);
    ctx.lineTo(x, y);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
    ctx.beginPath();
    ctx.moveTo(x - r, y + r);
    ctx.lineTo(x + r, y + r);
    ctx.lineTo(x, y);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.35;
    }
    ctx.strokeStyle = 'rgba(255,255,255,0.65)';
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.04);
    hexPath();
    ctx.stroke();
    ctx.shadowBlur = 0;
  }

  /** Sharp electric spark — the same 8-point star used for `magic`'s sparkle
   *  read differently once it's this saturated/glowy and paired with
   *  lightning's blue-white palette instead of magic's purple twinkle. */
  private drawLightningDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const r = radius * 1.15;

    ctx.shadowColor = base;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.45 : 0.24);
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, this.themeColor('--canvas-dot-highlight', '#FFFFFF'));
    grad.addColorStop(0.35, light);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    this.drawSparklePath(x, y, r);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.035);
    this.drawSparklePath(x, y, r * 0.98);
    ctx.stroke();
  }

  /** Thick metal ring, stroked rather than filled — the same "loop of wire"
   *  read as `drawChainRing()` but big and round enough to anchor a pair. */
  private drawChainDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const r = radius * 0.78;
    const wireW = radius * 0.52;

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.22)';
    ctx.lineWidth = wireW * 1.1;
    ctx.beginPath();
    ctx.arc(x, y + wireW * 0.12, r, 0, Math.PI * 2);
    ctx.stroke();

    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.35;
    }
    ctx.strokeStyle = base;
    ctx.lineWidth = wireW;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.shadowBlur = 0;

    const grad = ctx.createLinearGradient(x - r, y - r, x + r, y + r);
    grad.addColorStop(0, light);
    grad.addColorStop(0.5, base);
    grad.addColorStop(1, light);
    ctx.strokeStyle = grad;
    ctx.lineWidth = wireW * 0.5;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.lineWidth = wireW * 0.2;
    ctx.beginPath();
    ctx.arc(x, y, r, Math.PI * 1.1, Math.PI * 1.9);
    ctx.stroke();
  }

  /** Orb base with a twisted-fiber texture drawn over it — rope's dot is a
   *  spliced rope-end knot, not a plain sphere. */
  private drawRopeDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    this.paintOrbBase(x, y, radius, base, light);

    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.clip();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = Math.max(1, radius * 0.12);
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.moveTo(x - radius * 1.2, y + i * radius * 0.75 - radius * 0.6);
      ctx.quadraticCurveTo(x, y + i * radius * 0.75 + radius * 0.6, x + radius * 1.2, y + i * radius * 0.75 - radius * 0.6);
      ctx.stroke();
    }
    ctx.restore();

    this.paintDotRing(x, y, radius, base, isActive);
  }

  /** Pearl droplet with rippling halo rings instead of a hard ring border —
   *  matches wave's water motif. */
  private drawWaveDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    ctx.strokeStyle = light;
    ctx.globalAlpha = 0.4;
    ctx.lineWidth = Math.max(1, radius * 0.08);
    ctx.beginPath();
    ctx.arc(x, y, radius * 1.35, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;

    this.paintOrbBase(x, y, radius, base, light);
    this.paintOrbSpecular(x, y, radius);
    this.paintDotRing(x, y, radius, base, isActive);
  }

  /** Coil viewed end-on — concentric rings instead of a solid disc. */
  private drawSpringDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.3;
    }
    const rings = 3;
    for (let i = rings; i >= 1; i--) {
      const r = (radius * i) / rings;
      ctx.strokeStyle = i % 2 === 0 ? light : base;
      ctx.lineWidth = Math.max(2, (radius / rings) * 0.7);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.shadowBlur = 0;
    this.paintDotRing(x, y, radius, base, isActive);
  }

  /** Button/spool — small orb with two thread holes and a stitch, echoing the
   *  fiber-tick texture in `drawThreadSegment`. */
  private drawThreadDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    this.paintOrbBase(x, y, radius, base, light);

    ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
    const holeR = radius * 0.14;
    ctx.beginPath();
    ctx.arc(x - radius * 0.28, y, holeR, 0, Math.PI * 2);
    ctx.arc(x + radius * 0.28, y, holeR, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = light;
    ctx.globalAlpha = 0.8;
    ctx.lineWidth = Math.max(1, radius * 0.08);
    ctx.beginPath();
    ctx.moveTo(x - radius * 0.28, y);
    ctx.lineTo(x + radius * 0.28, y);
    ctx.stroke();
    ctx.globalAlpha = 1;

    this.paintDotRing(x, y, radius, base, isActive);
  }

  /** Bubble — bigger, glossier version of `drawBubbleNode`'s look, plus a
   *  tiny satellite bubble so it doesn't read as a plain sphere. */
  private drawBubbleDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    this.paintOrbBase(x, y, radius, base, light);

    ctx.globalAlpha = 0.85;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.lineWidth = Math.max(1.5, radius * 0.1);
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.7, 0.2 * Math.PI, 1.1 * Math.PI);
    ctx.stroke();
    ctx.globalAlpha = 1;

    const satR = radius * 0.22;
    const satX = x + radius * 0.85;
    const satY = y - radius * 0.75;
    const satGrad = ctx.createRadialGradient(satX - satR * 0.3, satY - satR * 0.3, 0, satX, satY, satR);
    satGrad.addColorStop(0, light);
    satGrad.addColorStop(1, base);
    ctx.fillStyle = satGrad;
    ctx.beginPath();
    ctx.arc(satX, satY, satR, 0, Math.PI * 2);
    ctx.fill();

    this.paintDotRing(x, y, radius, base, isActive);
  }

  /** Bow — two loops and a center knot, no circular ring (the bow's own
   *  outline reads as the boundary). */
  private drawRibbonDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.3;
    }
    const grad = ctx.createLinearGradient(x - radius, y, x + radius, y);
    grad.addColorStop(0, light);
    grad.addColorStop(0.5, base);
    grad.addColorStop(1, light);
    ctx.fillStyle = grad;

    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(
        x + side * radius * 1.3,
        y - radius * 0.9,
        x + side * radius * 1.05,
        y,
      );
      ctx.quadraticCurveTo(
        x + side * radius * 1.3,
        y + radius * 0.9,
        x,
        y,
      );
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = Math.max(1, radius * 0.05);
      ctx.stroke();
    }
    ctx.shadowBlur = 0;

    ctx.fillStyle = base;
    this.roundRect(ctx, x - radius * 0.22, y - radius * 0.3, radius * 0.44, radius * 0.6, radius * 0.12);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = Math.max(1, radius * 0.05);
    ctx.stroke();
  }

  /** Orb with a flame lick on top — warmer highlight than the plain sphere. */
  private drawFireDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    this.paintOrbBase(x, y, radius, base, light);
    this.paintOrbSpecular(x, y, radius);

    ctx.shadowColor = base;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.35 : 0.18);
    const flameGrad = ctx.createLinearGradient(x, y - radius * 1.5, x, y - radius * 0.3);
    flameGrad.addColorStop(0, 'rgba(255, 235, 160, 0.95)');
    flameGrad.addColorStop(1, light);
    ctx.fillStyle = flameGrad;
    ctx.beginPath();
    ctx.moveTo(x, y - radius * 1.55);
    ctx.quadraticCurveTo(x + radius * 0.4, y - radius * 0.95, x + radius * 0.16, y - radius * 0.45);
    ctx.quadraticCurveTo(x, y - radius * 0.6, x - radius * 0.16, y - radius * 0.45);
    ctx.quadraticCurveTo(x - radius * 0.4, y - radius * 0.95, x, y - radius * 1.55);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;

    this.paintDotRing(x, y, radius, base, isActive);
  }

  /** Orb with a small leaf accent — vine's dot is a bud, not a plain sphere. */
  private drawVineDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    this.paintOrbBase(x, y, radius, base, light);
    this.paintOrbSpecular(x, y, radius);

    ctx.fillStyle = light;
    ctx.beginPath();
    ctx.ellipse(x, y - radius * 1.15, radius * 0.42, radius * 0.24, -0.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = base;
    ctx.lineWidth = Math.max(1, radius * 0.05);
    ctx.beginPath();
    ctx.moveTo(x - radius * 0.25, y - radius * 1.3);
    ctx.lineTo(x + radius * 0.25, y - radius * 1.0);
    ctx.stroke();

    this.paintDotRing(x, y, radius, base, isActive);
  }

  /** Horseshoe magnet — red/blue poles, no circular ring (the horseshoe's own
   *  silhouette is the boundary). Bigger cousin of `drawMagnetNode`. */
  private drawMagnetDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const r = radius * 1.05;

    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.3;
    }

    ctx.fillStyle = '#DC2626';
    ctx.beginPath();
    ctx.arc(x, y, r, -Math.PI / 2, Math.PI / 2);
    ctx.closePath();
    ctx.fill();

    const grad = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, 0, x, y, r);
    grad.addColorStop(0, light);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, r, Math.PI / 2, Math.PI * 1.5);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.lineWidth = Math.max(1.5, r * 0.1);
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.lineTo(x, y + r);
    ctx.stroke();

    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.font = `${Math.round(r * 0.55)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('N', x + r * 0.5, y);
    ctx.fillText('S', x - r * 0.5, y);
  }

  /** Swirled lollipop — bigger cousin of `drawCandyNode`'s spiral. */
  private drawCandyDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.3;
    }
    ctx.fillStyle = light;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.clip();
    ctx.strokeStyle = base;
    ctx.lineWidth = radius * 0.32;
    ctx.beginPath();
    for (let a = 0; a < Math.PI * 4; a += 0.2) {
      const rad = (a / (Math.PI * 4)) * radius * 1.3;
      const px = x + Math.cos(a) * rad;
      const py = y + Math.sin(a) * rad;
      if (a === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
    ctx.restore();

    this.paintDotRing(x, y, radius, base, isActive);
  }

  /** Dog bone — literal, matches the style's name. No circular ring. */
  private drawBoneDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.3;
    }
    const grad = ctx.createRadialGradient(x - radius * 0.3, y - radius * 0.3, 0, x, y, radius);
    grad.addColorStop(0, light);
    grad.addColorStop(0.6, base);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;

    const armR = radius * 0.42;
    ctx.beginPath();
    ctx.arc(x - radius * 0.6, y - armR * 0.9, armR, 0, Math.PI * 2);
    ctx.arc(x - radius * 0.6, y + armR * 0.9, armR, 0, Math.PI * 2);
    ctx.arc(x + radius * 0.6, y - armR * 0.9, armR, 0, Math.PI * 2);
    ctx.arc(x + radius * 0.6, y + armR * 0.9, armR, 0, Math.PI * 2);
    ctx.fill();
    this.roundRect(ctx, x - radius * 0.65, y - radius * 0.32, radius * 1.3, radius * 0.64, radius * 0.2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = Math.max(1, radius * 0.05);
    ctx.stroke();
  }

  /** Sparkle burst with two orbiting mini stars — bigger, busier cousin of
   *  `drawMagicNode` so the endpoint reads as "the important one". */
  private drawMagicDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    ctx.shadowColor = base;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.45 : 0.22);

    const grad = ctx.createRadialGradient(x, y, 0, x, y, radius * 1.2);
    grad.addColorStop(0, this.themeColor('--canvas-dot-highlight', '#FFFFFF'));
    grad.addColorStop(0.5, light);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    this.drawSparklePath(x, y, radius * 1.15);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    this.drawSparklePath(x, y, radius * 0.48);
    ctx.fill();

    ctx.globalAlpha = 0.85;
    ctx.fillStyle = light;
    this.drawSparklePath(x + radius * 0.95, y - radius * 0.75, radius * 0.22);
    ctx.fill();
    this.drawSparklePath(x - radius * 0.85, y + radius * 0.85, radius * 0.16);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  /** Dark PCB-black tube + glowing neon edge/center trace + small square
   *  "SMD chip" pads sticking off both sides — added 2026-09-19 per user
   *  request for a "tough" theme (the existing 18 skewed cute/soft). */
  private drawCyberpunkSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const dark = this.darkenColor(base, 0.72);

    const drawTube = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half * 0.25);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half * 0.25);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    ctx.fillStyle = dark;
    drawTube();
    ctx.fill();

    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.32 : 0.16);
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.03);
    drawTube();
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.globalAlpha *= 0.85;
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.016);
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();

    ctx.globalAlpha = isActive ? 0.95 : 0.75;
    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const chipCount = Math.max(1, Math.floor(length / (this.cellSize * 0.4)));
    ctx.fillStyle = base;
    ctx.strokeStyle = light;
    ctx.lineWidth = 1;
    const chipPhase = (segIndex % 2) * 0.5;
    for (let i = 0; i < chipCount; i++) {
      const t = ((i + 0.5) / chipCount + chipPhase) % 1;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const chipSize = half * 0.5;
      if (isHorizontal) {
        ctx.fillRect(cx - chipSize / 2, cy - half * 0.85, chipSize, half * 0.35);
        ctx.strokeRect(cx - chipSize / 2, cy - half * 0.85, chipSize, half * 0.35);
        ctx.fillRect(cx - chipSize / 2, cy + half * 0.5, chipSize, half * 0.35);
        ctx.strokeRect(cx - chipSize / 2, cy + half * 0.5, chipSize, half * 0.35);
      } else {
        ctx.fillRect(cx - half * 0.85, cy - chipSize / 2, half * 0.35, chipSize);
        ctx.strokeRect(cx - half * 0.85, cy - chipSize / 2, half * 0.35, chipSize);
        ctx.fillRect(cx + half * 0.5, cy - chipSize / 2, half * 0.35, chipSize);
        ctx.strokeRect(cx + half * 0.5, cy - chipSize / 2, half * 0.35, chipSize);
      }
    }

    ctx.restore();
  }

  private drawCyberpunkNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.5;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.28 : 0.12);
    ctx.fillStyle = this.darkenColor(base, 0.6);
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.022);
    this.roundRect(ctx, cx - r, cy - r, r * 2, r * 2, r * 0.25);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.fillStyle = light;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /** IC-chip endpoint: dark body, glowing neon border, four little "leg" pins
   *  per side, glowing die in the center. */
  private drawCyberpunkDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const size = radius * 1.3;
    const dark = this.darkenColor(base, 0.68);

    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(0, 0, 0, 0.4)');
    ctx.shadowBlur = this.cellSize * 0.14;
    ctx.fillStyle = dark;
    this.roundRect(ctx, x - size / 2, y - size / 2, size, size, size * 0.16);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, radius * 0.09);
    const pinLen = radius * 0.28;
    for (const p of [-0.5, 0, 0.5]) {
      ctx.beginPath();
      ctx.moveTo(x + p * size, y - size / 2);
      ctx.lineTo(x + p * size, y - size / 2 - pinLen);
      ctx.moveTo(x + p * size, y + size / 2);
      ctx.lineTo(x + p * size, y + size / 2 + pinLen);
      ctx.moveTo(x - size / 2, y + p * size);
      ctx.lineTo(x - size / 2 - pinLen, y + p * size);
      ctx.moveTo(x + size / 2, y + p * size);
      ctx.lineTo(x + size / 2 + pinLen, y + p * size);
      ctx.stroke();
    }

    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.4 : 0.2);
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, radius * 0.08);
    this.roundRect(ctx, x - size / 2, y - size / 2, size, size, size * 0.16);
    ctx.stroke();
    ctx.shadowBlur = 0;

    const dieSize = size * 0.42;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, dieSize);
    grad.addColorStop(0, this.themeColor('--canvas-dot-highlight', '#FFFFFF'));
    grad.addColorStop(0.5, light);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    this.roundRect(ctx, x - dieSize / 2, y - dieSize / 2, dieSize, dieSize, dieSize * 0.15);
    ctx.fill();

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.5;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.lineWidth = 2;
      this.roundRect(ctx, x - size / 2 - 2, y - size / 2 - 2, size + 4, size + 4, size * 0.18);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  /** Dark wrought-iron tube with thorn-like spikes jutting off alternating
   *  sides + a thin moonlit glint down the middle — added 2026-09-19
   *  alongside `cyberpunk` for the same "tough theme" request. */
  private drawGothSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const dark = this.darkenColor(base, 0.75);
    const darker = this.darkenColor(base, 0.88);

    const drawTube = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half * 0.4);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half * 0.4);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    ctx.fillStyle = dark;
    drawTube();
    ctx.fill();

    const crossGrad = isHorizontal
      ? ctx.createLinearGradient(0, p1.y - half, 0, p1.y + half)
      : ctx.createLinearGradient(p1.x - half, 0, p1.x + half, 0);
    crossGrad.addColorStop(0, 'rgba(0, 0, 0, 0.5)');
    crossGrad.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
    crossGrad.addColorStop(1, 'rgba(0, 0, 0, 0.5)');
    ctx.fillStyle = crossGrad;
    drawTube();
    ctx.fill();

    const savedAlpha = ctx.globalAlpha;
    ctx.strokeStyle = light;
    ctx.globalAlpha = savedAlpha * 0.55;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.018);
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();
    ctx.globalAlpha = savedAlpha;

    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const spikeCount = Math.max(1, Math.floor(length / (this.cellSize * 0.45)));
    ctx.fillStyle = darker;
    ctx.strokeStyle = light;
    ctx.lineWidth = 1;
    for (let i = 0; i < spikeCount; i++) {
      const t = (i + 0.5) / spikeCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const flip = (i + segIndex) % 2 === 0 ? 1 : -1;
      const spikeLen = half * 0.9;
      ctx.beginPath();
      if (isHorizontal) {
        ctx.moveTo(cx - half * 0.3, cy - flip * half);
        ctx.lineTo(cx, cy - flip * (half + spikeLen));
        ctx.lineTo(cx + half * 0.3, cy - flip * half);
      } else {
        ctx.moveTo(cx - flip * half, cy - half * 0.3);
        ctx.lineTo(cx - flip * (half + spikeLen), cy);
        ctx.lineTo(cx - flip * half, cy + half * 0.3);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawGothNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.55;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    ctx.fillStyle = this.darkenColor(base, 0.8);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.02);
    ctx.stroke();

    ctx.globalAlpha *= 0.75;
    ctx.beginPath();
    ctx.moveTo(cx - r * 0.4, cy);
    ctx.lineTo(cx + r * 0.4, cy);
    ctx.moveTo(cx, cy - r * 0.4);
    ctx.lineTo(cx, cy + r * 0.4);
    ctx.stroke();
    ctx.restore();
  }

  /** Iron medallion — dark orb, a "crown of thorns" spike ring, and a
   *  glowing cross accent instead of the shared glass-orb `paintOrbBase`
   *  look (deliberately darker/moodier, matching the tube's wrought-iron
   *  material rather than pretending to be a gem). */
  private drawGothDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const darker = this.darkenColor(base, 0.85);

    ctx.fillStyle = darker;
    ctx.strokeStyle = light;
    ctx.lineWidth = 1;
    const spikeCount = 8;
    for (let i = 0; i < spikeCount; i++) {
      const angle = (Math.PI * 2 * i) / spikeCount;
      const baseR = radius * 0.95;
      const tipR = radius * 1.35;
      const spread = (Math.PI / spikeCount) * 0.5;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(angle - spread) * baseR, y + Math.sin(angle - spread) * baseR);
      ctx.lineTo(x + Math.cos(angle) * tipR, y + Math.sin(angle) * tipR);
      ctx.lineTo(x + Math.cos(angle + spread) * baseR, y + Math.sin(angle + spread) * baseR);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    this.paintOrbBase(x, y, radius * 0.9, this.darkenColor(base, 0.7), this.darkenColor(base, 0.35));

    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.35 : 0.15);
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.028);
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.9, 0, Math.PI * 2);
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, radius * 0.1);
    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * 0.2;
    ctx.beginPath();
    ctx.moveTo(x, y - radius * 0.5);
    ctx.lineTo(x, y + radius * 0.5);
    ctx.moveTo(x - radius * 0.3, y - radius * 0.15);
    ctx.lineTo(x + radius * 0.3, y - radius * 0.15);
    ctx.stroke();
    ctx.shadowBlur = 0;

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.45;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, radius * 1.4, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  /** Gunmetal tube with rivets along both edges and a seam at the midpoint —
   *  one of 10 styles added 2026-09-19 alongside cyberpunk/goth (user: "come
   *  up with 10 more similar yourself") to round the "tough" family out. */
  private drawRobotSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const metal = this.darkenColor(base, 0.55);

    const drawTube = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half * 0.3);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half * 0.3);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    ctx.fillStyle = metal;
    drawTube();
    ctx.fill();

    ctx.strokeStyle = light;
    ctx.globalAlpha *= 0.5;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.02);
    drawTube();
    ctx.stroke();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const rivetCount = Math.max(1, Math.floor(length / (this.cellSize * 0.4)));
    const phase = (segIndex % 2) * 0.5;
    ctx.fillStyle = light;
    for (let i = 0; i < rivetCount; i++) {
      const t = ((i + 0.5) / rivetCount + phase) % 1;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const rivetR = half * 0.14;
      if (isHorizontal) {
        ctx.beginPath();
        ctx.arc(cx, cy - half * 0.65, rivetR, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx, cy + half * 0.65, rivetR, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.arc(cx - half * 0.65, cy, rivetR, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx + half * 0.65, cy, rivetR, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.strokeStyle = this.darkenColor(base, 0.75);
    ctx.lineWidth = Math.max(1, this.cellSize * 0.025);
    const midX = (p1.x + p2.x) / 2;
    const midY = (p1.y + p2.y) / 2;
    ctx.beginPath();
    if (isHorizontal) {
      ctx.moveTo(midX, midY - half * 0.9);
      ctx.lineTo(midX, midY + half * 0.9);
    } else {
      ctx.moveTo(midX - half * 0.9, midY);
      ctx.lineTo(midX + half * 0.9, midY);
    }
    ctx.stroke();

    ctx.restore();
  }

  private drawRobotNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.55;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    ctx.fillStyle = this.darkenColor(base, 0.55);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.02);
    ctx.stroke();

    ctx.strokeStyle = this.darkenColor(base, 0.85);
    ctx.lineWidth = Math.max(1, this.cellSize * 0.018);
    ctx.beginPath();
    ctx.moveTo(cx - r * 0.5, cy);
    ctx.lineTo(cx + r * 0.5, cy);
    ctx.moveTo(cx, cy - r * 0.5);
    ctx.lineTo(cx, cy + r * 0.5);
    ctx.stroke();
    ctx.restore();
  }

  /** Armored joint capsule — a rounded square with corner rivets and a
   *  glowing screw-slot core, deliberately shaped differently from
   *  `cyberpunk`'s IC-chip dot (no pins) despite the shared mechanical
   *  vocabulary. */
  private drawRobotDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const size = radius * 1.5;
    const metal = this.darkenColor(base, 0.5);

    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(0, 0, 0, 0.4)');
    ctx.shadowBlur = this.cellSize * 0.14;
    ctx.fillStyle = metal;
    this.roundRect(ctx, x - size / 2, y - size / 2, size, size, size * 0.3);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, radius * 0.08);
    this.roundRect(ctx, x - size / 2, y - size / 2, size, size, size * 0.3);
    ctx.stroke();

    ctx.fillStyle = light;
    const rivetR = radius * 0.09;
    for (const [dx, dy] of [
      [-1, -1],
      [1, -1],
      [-1, 1],
      [1, 1],
    ]) {
      ctx.beginPath();
      ctx.arc(x + dx * size * 0.36, y + dy * size * 0.36, rivetR, 0, Math.PI * 2);
      ctx.fill();
    }

    const coreR = radius * 0.32;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, coreR);
    grad.addColorStop(0, this.themeColor('--canvas-dot-highlight', '#FFFFFF'));
    grad.addColorStop(0.5, light);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, coreR, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = this.darkenColor(base, 0.7);
    ctx.lineWidth = Math.max(1, radius * 0.05);
    ctx.beginPath();
    ctx.moveTo(x - coreR * 0.6, y);
    ctx.lineTo(x + coreR * 0.6, y);
    ctx.moveTo(x, y - coreR * 0.6);
    ctx.lineTo(x, y + coreR * 0.6);
    ctx.stroke();

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.4;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 2;
      this.roundRect(ctx, x - size / 2 - 2, y - size / 2 - 2, size + 4, size + 4, size * 0.32);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  /** Black rubber tread tube with a colored sidewall stripe. */
  private drawWheelSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const rubber = this.darkenColor(base, 0.82);

    const drawTube = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half * 0.5);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half * 0.5);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    ctx.fillStyle = rubber;
    drawTube();
    ctx.fill();

    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.02);
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();

    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const treadCount = Math.max(2, Math.floor(length / (this.cellSize * 0.22)));
    const phase = (segIndex % 2) * (0.5 / treadCount);
    ctx.fillStyle = this.darkenColor(base, 0.92);
    for (let i = 0; i < treadCount; i++) {
      const t = ((i + 0.5) / treadCount + phase) % 1;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const w = half * 0.3;
      const l = half * 1.7;
      if (isHorizontal) ctx.fillRect(cx - w / 2, cy - l / 2, w, l);
      else ctx.fillRect(cx - l / 2, cy - w / 2, l, w);
    }

    ctx.restore();
  }

  private drawWheelNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.55;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    ctx.fillStyle = this.darkenColor(base, 0.82);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.02);
    ctx.stroke();
    for (let i = 0; i < 4; i++) {
      const a = (Math.PI / 2) * i;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a) * r * 0.7, cy + Math.sin(a) * r * 0.7);
      ctx.stroke();
    }
    ctx.restore();
  }

  /** Full hubcap — dark tire ring, metallic rim gradient, 5 spokes, center
   *  cap. */
  private drawWheelDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const tireR = radius * 1.05;
    const rimR = radius * 0.72;

    ctx.strokeStyle = this.darkenColor(base, 0.85);
    ctx.lineWidth = radius * 0.4;
    ctx.beginPath();
    ctx.arc(x, y, tireR - radius * 0.2, 0, Math.PI * 2);
    ctx.stroke();

    const grad = ctx.createRadialGradient(x - rimR * 0.3, y - rimR * 0.3, rimR * 0.1, x, y, rimR);
    grad.addColorStop(0, this.themeColor('--canvas-dot-highlight', '#FFFFFF'));
    grad.addColorStop(0.4, light);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, rimR, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = this.darkenColor(base, 0.5);
    ctx.lineWidth = Math.max(1.5, radius * 0.09);
    for (let i = 0; i < 5; i++) {
      const a = ((Math.PI * 2) / 5) * i - Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(a) * rimR * 0.85, y + Math.sin(a) * rimR * 0.85);
      ctx.stroke();
    }

    ctx.fillStyle = this.darkenColor(base, 0.4);
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.22, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = light;
    ctx.lineWidth = 1;
    ctx.stroke();

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.4;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, tireR + 3, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  /** Black tube with a jagged pink/purple fringe streak and safety-pin X
   *  ticks. */
  private drawEmoSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const black = this.darkenColor(base, 0.88);

    const drawTube = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half * 0.3);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half * 0.3);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    ctx.fillStyle = black;
    drawTube();
    ctx.fill();

    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const zigCount = Math.max(2, Math.round(length / (this.cellSize * 0.28)));
    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.22 : 0.1);
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.03);
    ctx.lineJoin = 'round';
    ctx.beginPath();
    for (let i = 0; i <= zigCount; i++) {
      const t = i / zigCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const side = (i + segIndex) % 2 === 0 ? 1 : -1;
      const off = i === 0 || i === zigCount ? 0 : half * 0.5 * side;
      const px = isHorizontal ? cx : cx + off;
      const py = isHorizontal ? cy + off : cy;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
    ctx.shadowBlur = 0;

    const pinCount = Math.max(1, Math.floor(length / (this.cellSize * 0.5)));
    ctx.globalAlpha *= 0.85;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.02);
    for (let i = 0; i < pinCount; i++) {
      const t = (i + 0.5) / pinCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const s = half * 0.5;
      ctx.beginPath();
      ctx.moveTo(cx - s / 2, cy - s / 2);
      ctx.lineTo(cx + s / 2, cy + s / 2);
      ctx.moveTo(cx + s / 2, cy - s / 2);
      ctx.lineTo(cx - s / 2, cy + s / 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawEmoNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.5;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    ctx.fillStyle = this.darkenColor(base, 0.88);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.25 : 0.1);
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.025);
    const s = r * 0.6;
    ctx.beginPath();
    ctx.moveTo(cx - s, cy - s);
    ctx.lineTo(cx + s, cy + s);
    ctx.moveTo(cx + s, cy - s);
    ctx.lineTo(cx - s, cy + s);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  /** Choppy jagged-fringe silhouette with a glowing cracked-heart accent. */
  private drawEmoDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const black = this.darkenColor(base, 0.88);

    const spikeCount = 10;
    ctx.fillStyle = black;
    ctx.beginPath();
    for (let i = 0; i < spikeCount; i++) {
      const angle = (Math.PI * 2 * i) / spikeCount;
      const r = i % 2 === 0 ? radius * 1.3 : radius * 0.85;
      const px = x + Math.cos(angle) * r;
      const py = y + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1, radius * 0.05);
    ctx.stroke();

    ctx.fillStyle = black;
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.62, 0, Math.PI * 2);
    ctx.fill();

    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.32 : 0.14);
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, radius * 0.09);
    const hr = radius * 0.28;
    ctx.beginPath();
    ctx.arc(x - hr * 0.55, y - hr * 0.2, hr * 0.55, Math.PI, 0);
    ctx.arc(x + hr * 0.55, y - hr * 0.2, hr * 0.55, Math.PI, 0);
    ctx.lineTo(x, y + hr * 0.9);
    ctx.closePath();
    ctx.stroke();
    ctx.shadowBlur = 0;

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.45;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, radius * 1.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  /** Flat riveted armor plate with seam lines between plate sections. */
  private drawArmorSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const steel = this.darkenColor(base, 0.6);

    const drawTube = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half * 0.12);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half * 0.12);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    ctx.fillStyle = steel;
    drawTube();
    ctx.fill();

    ctx.strokeStyle = light;
    ctx.globalAlpha *= 0.55;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.025);
    drawTube();
    ctx.stroke();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const plateCount = Math.max(1, Math.floor(length / (this.cellSize * 0.5)));
    ctx.strokeStyle = this.darkenColor(base, 0.8);
    ctx.lineWidth = Math.max(1, this.cellSize * 0.022);
    for (let i = 1; i < plateCount; i++) {
      const t = i / plateCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      ctx.beginPath();
      if (isHorizontal) {
        ctx.moveTo(cx, cy - half);
        ctx.lineTo(cx, cy + half);
      } else {
        ctx.moveTo(cx - half, cy);
        ctx.lineTo(cx + half, cy);
      }
      ctx.stroke();
    }

    const rivetCount = Math.max(1, Math.floor(length / (this.cellSize * 0.32)));
    const phase = (segIndex % 2) * 0.5;
    ctx.fillStyle = light;
    for (let i = 0; i < rivetCount; i++) {
      const t = ((i + 0.5) / rivetCount + phase) % 1;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      ctx.beginPath();
      ctx.arc(cx, cy, half * 0.14, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private drawArmorNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.5;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    ctx.fillStyle = this.darkenColor(base, 0.6);
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 6;
      const px = cx + Math.cos(a) * r;
      const py = cy + Math.sin(a) * r;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.02);
    ctx.stroke();
    ctx.fillStyle = light;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.25, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /** Hexagonal armor plate with 6 rim rivets and a glowing reactor core. */
  private drawArmorDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const steel = this.darkenColor(base, 0.55);
    const hexR = radius * 1.15;

    const hexPath = (r: number) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        const px = x + Math.cos(a) * r;
        const py = y + Math.sin(a) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
    };

    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(0, 0, 0, 0.4)');
    ctx.shadowBlur = this.cellSize * 0.14;
    ctx.fillStyle = steel;
    hexPath(hexR);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, radius * 0.08);
    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.32 : 0.14);
    hexPath(hexR);
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.fillStyle = light;
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      const px = x + Math.cos(a) * hexR * 0.82;
      const py = y + Math.sin(a) * hexR * 0.82;
      ctx.beginPath();
      ctx.arc(px, py, radius * 0.08, 0, Math.PI * 2);
      ctx.fill();
    }

    const coreR = radius * 0.36;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, coreR);
    grad.addColorStop(0, this.themeColor('--canvas-dot-highlight', '#FFFFFF'));
    grad.addColorStop(0.5, light);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, coreR, 0, Math.PI * 2);
    ctx.fill();

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.45;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 2;
      hexPath(hexR * 1.25);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  /** Chrome pipe with a hot specular streak down the middle and exhaust-pipe
   *  ridge grooves. */
  private drawChromeSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;

    const drawTube = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    const crossGrad = isHorizontal
      ? ctx.createLinearGradient(0, p1.y - half, 0, p1.y + half)
      : ctx.createLinearGradient(p1.x - half, 0, p1.x + half, 0);
    crossGrad.addColorStop(0, this.darkenColor(base, 0.55));
    crossGrad.addColorStop(0.25, light);
    crossGrad.addColorStop(0.42, '#FFFFFF');
    crossGrad.addColorStop(0.58, light);
    crossGrad.addColorStop(1, this.darkenColor(base, 0.55));
    ctx.fillStyle = crossGrad;
    drawTube();
    ctx.fill();

    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const grooveCount = Math.max(2, Math.floor(length / (this.cellSize * 0.3)));
    const phase = (segIndex % 2) * (0.5 / grooveCount);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.28)';
    ctx.lineWidth = Math.max(1, this.cellSize * 0.015);
    for (let i = 0; i < grooveCount; i++) {
      const t = ((i + 0.5) / grooveCount + phase) % 1;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      ctx.beginPath();
      if (isHorizontal) {
        ctx.moveTo(cx, cy - half * 0.9);
        ctx.lineTo(cx, cy + half * 0.9);
      } else {
        ctx.moveTo(cx - half * 0.9, cy);
        ctx.lineTo(cx + half * 0.9, cy);
      }
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawChromeNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.55;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    const grad = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.4, r * 0.05, cx, cy, r);
    grad.addColorStop(0, '#FFFFFF');
    grad.addColorStop(0.35, light);
    grad.addColorStop(0.75, this.darkenColor(base, 0.4));
    grad.addColorStop(1, this.darkenColor(base, 0.6));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /** Chrome piston cap — concentric groove rings and a hot specular streak. */
  private drawChromeDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;

    const grad = ctx.createRadialGradient(x - radius * 0.35, y - radius * 0.4, radius * 0.05, x, y, radius);
    grad.addColorStop(0, '#FFFFFF');
    grad.addColorStop(0.3, light);
    grad.addColorStop(0.7, this.darkenColor(base, 0.35));
    grad.addColorStop(1, this.darkenColor(base, 0.55));
    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(0, 0, 0, 0.35)');
    ctx.shadowBlur = this.cellSize * 0.16;
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.25)';
    ctx.lineWidth = Math.max(1, radius * 0.05);
    for (const f of [0.45, 0.68]) {
      ctx.beginPath();
      ctx.arc(x, y, radius * f, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = Math.max(1.5, radius * 0.1);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.8, -Math.PI * 0.85, -Math.PI * 0.55);
    ctx.stroke();
    ctx.lineCap = 'butt';

    ctx.fillStyle = this.darkenColor(base, 0.5);
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.22, 0, Math.PI * 2);
    ctx.fill();

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.4;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, radius * 1.35, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  /** Leather-wrapped wooden haft with X-laced ties at intervals. */
  private drawVikingSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const wood = this.darkenColor(base, 0.65);

    const drawTube = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half * 0.3);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half * 0.3);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    ctx.fillStyle = wood;
    drawTube();
    ctx.fill();

    const crossGrad = isHorizontal
      ? ctx.createLinearGradient(0, p1.y - half, 0, p1.y + half)
      : ctx.createLinearGradient(p1.x - half, 0, p1.x + half, 0);
    crossGrad.addColorStop(0, 'rgba(0, 0, 0, 0.35)');
    crossGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.08)');
    crossGrad.addColorStop(1, 'rgba(0, 0, 0, 0.35)');
    ctx.fillStyle = crossGrad;
    drawTube();
    ctx.fill();

    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const laceCount = Math.max(1, Math.floor(length / (this.cellSize * 0.45)));
    const phase = (segIndex % 2) * 0.5;
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.025);
    for (let i = 0; i < laceCount; i++) {
      const t = ((i + 0.5) / laceCount + phase) % 1;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      ctx.beginPath();
      if (isHorizontal) {
        ctx.moveTo(cx - half * 0.3, cy - half * 0.85);
        ctx.lineTo(cx + half * 0.3, cy + half * 0.85);
        ctx.moveTo(cx + half * 0.3, cy - half * 0.85);
        ctx.lineTo(cx - half * 0.3, cy + half * 0.85);
      } else {
        ctx.moveTo(cx - half * 0.85, cy - half * 0.3);
        ctx.lineTo(cx + half * 0.85, cy + half * 0.3);
        ctx.moveTo(cx + half * 0.85, cy - half * 0.3);
        ctx.lineTo(cx - half * 0.85, cy + half * 0.3);
      }
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawVikingNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.55;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    ctx.fillStyle = this.darkenColor(base, 0.65);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.022);
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.65, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  /** Wooden shield boss — cross-brace, rim rivets, glowing center. */
  private drawVikingDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const wood = this.darkenColor(base, 0.6);

    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(0, 0, 0, 0.35)');
    ctx.shadowBlur = this.cellSize * 0.14;
    ctx.fillStyle = wood;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, radius * 0.07);
    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.3 : 0.14);
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.9, 0, Math.PI * 2);
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, radius * 0.09);
    ctx.beginPath();
    ctx.moveTo(x - radius * 0.75, y);
    ctx.lineTo(x + radius * 0.75, y);
    ctx.moveTo(x, y - radius * 0.75);
    ctx.lineTo(x, y + radius * 0.75);
    ctx.stroke();

    const grad = ctx.createRadialGradient(x, y, 0, x, y, radius * 0.32);
    grad.addColorStop(0, this.themeColor('--canvas-dot-highlight', '#FFFFFF'));
    grad.addColorStop(0.5, light);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.3, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = light;
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i;
      ctx.beginPath();
      ctx.arc(x + Math.cos(a) * radius * 0.9, y + Math.sin(a) * radius * 0.9, radius * 0.08, 0, Math.PI * 2);
      ctx.fill();
    }

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.45;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, radius * 1.35, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  /** Slim blade cross-section with a wavy "hamon" temper-line, instead of
   *  the usual rounded tube. */
  private drawSamuraiSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = ((this.cellSize * this.pipeRatio) / 2) * 0.7;
    const steel = this.darkenColor(base, 0.55);

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    const crossGrad = isHorizontal
      ? ctx.createLinearGradient(0, p1.y - half, 0, p1.y + half)
      : ctx.createLinearGradient(p1.x - half, 0, p1.x + half, 0);
    crossGrad.addColorStop(0, steel);
    crossGrad.addColorStop(0.5, light);
    crossGrad.addColorStop(1, steel);
    ctx.fillStyle = crossGrad;
    if (isHorizontal) {
      const left = Math.min(p1.x, p2.x);
      const width = Math.abs(p2.x - p1.x);
      this.roundRect(ctx, left, p1.y - half, width, half * 2, half * 0.15);
    } else {
      const top = Math.min(p1.y, p2.y);
      const height = Math.abs(p2.y - p1.y);
      this.roundRect(ctx, p1.x - half, top, half * 2, height, half * 0.15);
    }
    ctx.fill();

    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const waveCount = Math.max(2, Math.round(length / (this.cellSize * 0.3)));
    ctx.strokeStyle = '#FFFFFF';
    ctx.globalAlpha *= 0.55;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.018);
    ctx.beginPath();
    for (let i = 0; i <= waveCount; i++) {
      const t = i / waveCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const wobble = Math.sin(t * Math.PI * 4 + segIndex) * half * 0.35;
      const ox = isHorizontal ? 0 : wobble + half * 0.15;
      const oy = isHorizontal ? wobble + half * 0.15 : 0;
      if (i === 0) ctx.moveTo(cx + ox, cy + oy);
      else ctx.lineTo(cx + ox, cy + oy);
    }
    ctx.stroke();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    ctx.strokeStyle = steel;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.015);
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();

    ctx.restore();
  }

  private drawSamuraiNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.55;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    ctx.fillStyle = this.darkenColor(base, 0.4);
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx + r, cy);
    ctx.lineTo(cx, cy + r);
    ctx.lineTo(cx - r, cy);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.02);
    ctx.stroke();
    ctx.restore();
  }

  /** Tsuba (sword guard) — octagon disc with radial slits and a glowing
   *  center hole. */
  private drawSamuraiDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const steel = this.darkenColor(base, 0.4);

    const octPath = (r: number) => {
      ctx.beginPath();
      for (let i = 0; i < 8; i++) {
        const a = (Math.PI / 4) * i;
        const px = x + Math.cos(a) * r;
        const py = y + Math.sin(a) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
    };

    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(0, 0, 0, 0.4)');
    ctx.shadowBlur = this.cellSize * 0.14;
    ctx.fillStyle = steel;
    octPath(radius);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, radius * 0.07);
    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.32 : 0.14);
    octPath(radius * 0.92);
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = this.darkenColor(base, 0.6);
    ctx.lineWidth = Math.max(1, radius * 0.05);
    for (let i = 0; i < 4; i++) {
      const a = (Math.PI / 4) * i + Math.PI / 8;
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a) * radius * 0.3, y + Math.sin(a) * radius * 0.3);
      ctx.lineTo(x + Math.cos(a) * radius * 0.75, y + Math.sin(a) * radius * 0.75);
      ctx.stroke();
    }

    const coreR = radius * 0.24;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, coreR);
    grad.addColorStop(0, this.themeColor('--canvas-dot-highlight', '#FFFFFF'));
    grad.addColorStop(0.5, light);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, coreR, 0, Math.PI * 2);
    ctx.fill();

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.45;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, radius * 1.35, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  /** Sickly mottled flesh tube with stitch-mark ticks and a thread line. */
  private drawZombieSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const flesh = this.darkenColor(base, 0.55);

    const drawTube = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half * 0.6);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half * 0.6);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    ctx.fillStyle = flesh;
    drawTube();
    ctx.fill();

    const crossGrad = isHorizontal
      ? ctx.createLinearGradient(0, p1.y - half, 0, p1.y + half)
      : ctx.createLinearGradient(p1.x - half, 0, p1.x + half, 0);
    crossGrad.addColorStop(0, 'rgba(0, 0, 0, 0.3)');
    crossGrad.addColorStop(0.5, 'rgba(120, 160, 80, 0.12)');
    crossGrad.addColorStop(1, 'rgba(0, 0, 0, 0.3)');
    ctx.fillStyle = crossGrad;
    drawTube();
    ctx.fill();

    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const stitchCount = Math.max(2, Math.floor(length / (this.cellSize * 0.24)));
    const phase = (segIndex % 2) * 0.5;
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1, this.cellSize * 0.02);
    for (let i = 0; i < stitchCount; i++) {
      const t = ((i + 0.5) / stitchCount + phase) % 1;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const s = half * 0.55;
      ctx.beginPath();
      if (isHorizontal) {
        ctx.moveTo(cx, cy - s);
        ctx.lineTo(cx, cy + s);
      } else {
        ctx.moveTo(cx - s, cy);
        ctx.lineTo(cx + s, cy);
      }
      ctx.stroke();
    }
    ctx.globalAlpha *= 0.6;
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();

    ctx.restore();
  }

  private drawZombieNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.55;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    ctx.fillStyle = this.darkenColor(base, 0.55);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.022);
    const s = r * 0.55;
    ctx.beginPath();
    ctx.moveTo(cx - s, cy - s);
    ctx.lineTo(cx + s, cy + s);
    ctx.moveTo(cx + s, cy - s);
    ctx.lineTo(cx - s, cy + s);
    ctx.stroke();
    ctx.restore();
  }

  /** Eyeball endpoint — sclera, glowing iris, pupil, stitched scar. */
  private drawZombieDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const flesh = this.darkenColor(base, 0.5);

    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(0, 0, 0, 0.35)');
    ctx.shadowBlur = this.cellSize * 0.14;
    ctx.fillStyle = flesh;
    ctx.beginPath();
    ctx.arc(x, y, radius * 1.05, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.fillStyle = 'rgba(230, 230, 210, 0.92)';
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.82, 0, Math.PI * 2);
    ctx.fill();

    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.3 : 0.12);
    const irisGrad = ctx.createRadialGradient(x, y, 0, x, y, radius * 0.5);
    irisGrad.addColorStop(0, light);
    irisGrad.addColorStop(1, base);
    ctx.fillStyle = irisGrad;
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.46, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.fillStyle = '#0A0A0A';
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.2, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = this.darkenColor(base, 0.7);
    ctx.lineWidth = Math.max(1, radius * 0.06);
    ctx.beginPath();
    ctx.moveTo(x - radius * 0.95, y - radius * 0.5);
    ctx.lineTo(x - radius * 0.55, y - radius * 0.85);
    ctx.stroke();
    for (const t of [0.25, 0.5, 0.75]) {
      const sx = x - radius * 0.95 + radius * 0.4 * t;
      const sy = y - radius * 0.5 - radius * 0.35 * t;
      ctx.beginPath();
      ctx.moveTo(sx - 2, sy - 2);
      ctx.lineTo(sx + 2, sy + 2);
      ctx.stroke();
    }

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.4;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, radius * 1.4, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  /** Dark sludge tube with a glowing core stream and drip bubbles. */
  private drawToxicSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const dark = this.darkenColor(base, 0.7);

    const drawTube = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half * 0.5);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half * 0.5);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    ctx.fillStyle = dark;
    drawTube();
    ctx.fill();

    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.3 : 0.14);
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.03);
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();
    ctx.shadowBlur = 0;

    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const dripCount = Math.max(1, Math.floor(length / (this.cellSize * 0.45)));
    const phase = (segIndex % 2) * 0.5;
    ctx.fillStyle = light;
    ctx.globalAlpha *= 0.8;
    for (let i = 0; i < dripCount; i++) {
      const t = ((i + 0.5) / dripCount + phase) % 1;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const r = half * 0.22;
      if (isHorizontal) {
        ctx.beginPath();
        ctx.arc(cx, cy + half * 0.95, r, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.arc(cx + half * 0.95, cy, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  private drawToxicNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.5;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    ctx.fillStyle = this.darkenColor(base, 0.7);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.25 : 0.1);
    ctx.fillStyle = light;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx + r * 0.55, cy - r * 0.55, r * 0.22, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  /** Biohazard-trefoil dot — dark orb with 3 glowing petal blobs. */
  private drawToxicDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const dark = this.darkenColor(base, 0.72);

    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(0, 0, 0, 0.35)');
    ctx.shadowBlur = this.cellSize * 0.14;
    ctx.fillStyle = dark;
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.95, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.3 : 0.14);
    ctx.fillStyle = light;
    for (let i = 0; i < 3; i++) {
      const a = ((Math.PI * 2) / 3) * i - Math.PI / 2;
      const petalX = x + Math.cos(a) * radius * 0.62;
      const petalY = y + Math.sin(a) * radius * 0.62;
      ctx.beginPath();
      ctx.arc(petalX, petalY, radius * 0.32, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.shadowBlur = 0;

    ctx.fillStyle = dark;
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.28, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1, radius * 0.05);
    ctx.stroke();

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.45;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, radius * 1.4, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  /** Charred obsidian tube with a glowing lava-crack zigzag. */
  private drawDemonSegment(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    light: string,
    isActive: boolean,
    segIndex: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const half = (this.cellSize * this.pipeRatio) / 2;
    const charred = this.darkenColor(base, 0.86);

    const drawTube = () => {
      if (isHorizontal) {
        const left = Math.min(p1.x, p2.x);
        const width = Math.abs(p2.x - p1.x);
        this.roundRect(ctx, left, p1.y - half, width, half * 2, half * 0.35);
      } else {
        const top = Math.min(p1.y, p2.y);
        const height = Math.abs(p2.y - p1.y);
        this.roundRect(ctx, p1.x - half, top, half * 2, height, half * 0.35);
      }
    };

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    ctx.fillStyle = charred;
    drawTube();
    ctx.fill();

    const length = isHorizontal ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y);
    const zigCount = Math.max(2, Math.round(length / (this.cellSize * 0.22)));
    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.32 : 0.14);
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.026);
    ctx.lineJoin = 'round';
    ctx.beginPath();
    for (let i = 0; i <= zigCount; i++) {
      const t = i / zigCount;
      const cx = p1.x + (p2.x - p1.x) * t;
      const cy = p1.y + (p2.y - p1.y) * t;
      const side = (i + segIndex) % 2 === 0 ? 1 : -1;
      const off = i === 0 || i === zigCount ? 0 : half * 0.45 * side;
      const px = isHorizontal ? cx : cx + off;
      const py = isHorizontal ? cy + off : cy;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.restore();
  }

  private drawDemonNode(
    x: number,
    y: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const { x: cx, y: cy } = this.posToPixel([x, y]);
    const r = ((this.cellSize * this.pipeRatio) / 2) * 0.5;

    ctx.save();
    ctx.globalAlpha = isActive ? 1 : 0.85;
    ctx.fillStyle = this.darkenColor(base, 0.86);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.28 : 0.12);
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.022);
    ctx.beginPath();
    ctx.moveTo(cx - r * 0.5, cy - r * 0.5);
    ctx.lineTo(cx + r * 0.5, cy + r * 0.5);
    ctx.moveTo(cx + r * 0.5, cy - r * 0.5);
    ctx.lineTo(cx - r * 0.5, cy + r * 0.5);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  /** Horned skull endpoint — charred body, two horns, glowing X eyes. */
  private drawDemonDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;
    const charred = this.darkenColor(base, 0.82);

    ctx.fillStyle = charred;
    ctx.strokeStyle = light;
    ctx.lineWidth = 1;
    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(x + side * radius * 0.35, y - radius * 0.55);
      ctx.lineTo(x + side * radius * 0.95, y - radius * 1.35);
      ctx.lineTo(x + side * radius * 0.75, y - radius * 0.35);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(0, 0, 0, 0.4)');
    ctx.shadowBlur = this.cellSize * 0.14;
    ctx.fillStyle = charred;
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.92, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, radius * 0.06);
    ctx.stroke();

    ctx.shadowColor = light;
    ctx.shadowBlur = this.cellSize * (isActive ? 0.32 : 0.14);
    ctx.strokeStyle = light;
    ctx.lineWidth = Math.max(1.5, radius * 0.1);
    for (const side of [-1, 1]) {
      const ex = x + side * radius * 0.36;
      const ey = y - radius * 0.05;
      const s = radius * 0.16;
      ctx.beginPath();
      ctx.moveTo(ex - s, ey - s);
      ctx.lineTo(ex + s, ey + s);
      ctx.moveTo(ex + s, ey - s);
      ctx.lineTo(ex - s, ey + s);
      ctx.stroke();
    }
    ctx.shadowBlur = 0;

    ctx.strokeStyle = light;
    ctx.globalAlpha = (isActive ? 1 : 0.85) * 0.7;
    ctx.lineWidth = Math.max(1, radius * 0.05);
    ctx.beginPath();
    ctx.moveTo(x, y + radius * 0.15);
    ctx.lineTo(x - radius * 0.15, y + radius * 0.45);
    ctx.lineTo(x + radius * 0.05, y + radius * 0.55);
    ctx.stroke();
    ctx.globalAlpha = isActive ? 1 : 0.85;

    if (isActive) {
      ctx.shadowColor = light;
      ctx.shadowBlur = this.cellSize * 0.45;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, radius * 1.45, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  }

  private drawSnakeDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
  ): void {
    const { ctx } = this;

    ctx.shadowColor = this.themeColor('--canvas-dot-shadow', 'rgba(20, 20, 40, 0.18)');
    ctx.shadowBlur = this.cellSize * 0.12;

    const grad = ctx.createRadialGradient(x - radius * 0.2, y - radius * 0.3, 0, x, y, radius);
    grad.addColorStop(0, light);
    grad.addColorStop(0.6, base);
    grad.addColorStop(1, base);
    ctx.fillStyle = grad;

    ctx.beginPath();
    ctx.ellipse(x, y, radius * 1.1, radius * 0.95, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.beginPath();
    ctx.arc(x - radius * 0.25, y - radius * 0.2, radius * 0.18, 0, Math.PI * 2);
    ctx.arc(x + radius * 0.25, y - radius * 0.2, radius * 0.18, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#1a1a2e';
    ctx.beginPath();
    ctx.arc(x - radius * 0.25, y - radius * 0.2, radius * 0.08, 0, Math.PI * 2);
    ctx.arc(x + radius * 0.25, y - radius * 0.2, radius * 0.08, 0, Math.PI * 2);
    ctx.fill();

    if (isActive) {
      ctx.shadowColor = base;
      ctx.shadowBlur = this.cellSize * 0.4;
    }
  }

  private drawGearDot(
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    isActive: boolean,
    colorId: string,
    gridPos: Pos,
    state: GameState,
  ): void {
    const { ctx } = this;
    const [gx, gy] = gridPos;
    const rotation = this.computeGearRotation(state, gx, gy);
    const flashMul = this.getEndpointFlashMul(colorId);
    const errorFx = this.getErrorVisuals(colorId);
    const pulse = isActive ? this.computeGearPulseScale() : 1;
    const pulsePhase = isActive ? this.computeGearPulsePhase() : 0;
    const teeth = 12;
    const outerR = radius * 1.05 * pulse * (errorFx ? 1 : flashMul);
    const innerR = radius * 0.75 * pulse * (errorFx ? 1 : flashMul);
    const sphereR = radius * 0.55 * pulse * flashMul;
    const brass = this.themeColor('--gear-wall-brass', '#8a6a3a');
    const brassHi = this.themeColor('--gear-wall-brass-highlight', '#c4a070');
    const copper = this.themeColor('--gear-wall-brass-highlight', '#6b4f2a');
    const gold = '#c4a070';

    ctx.save();
    if (errorFx) {
      ctx.translate(errorFx.dx, errorFx.dy);
      ctx.globalAlpha = errorFx.dim;
    }
    if (rotation !== 0) {
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.translate(-x, -y);
    }
    ctx.translate(x, y);
    ctx.scale(pulse, pulse);
    ctx.translate(-x, -y);

    const bloomBlur = isActive
      ? this.cellSize * (0.32 + pulsePhase * 0.14) * flashMul
      : this.cellSize * 0.22 * flashMul;
    ctx.shadowColor = base;
    ctx.shadowBlur = bloomBlur;

    const nestGrad = ctx.createRadialGradient(
      x - outerR * 0.15,
      y - outerR * 0.15,
      0,
      x,
      y,
      outerR,
    );
    nestGrad.addColorStop(0, brassHi);
    nestGrad.addColorStop(0.55, brass);
    nestGrad.addColorStop(1, brass);
    ctx.fillStyle = nestGrad;

    this.traceGearTeeth(x, y, outerR, Math.max(innerR, outerR * 0.82), teeth);
    ctx.fill();

    ctx.shadowBlur = 0;
    this.drawGearEndpointRim(colorId, x, y, radius, base, light, brass, brassHi, copper, gold);

    ctx.shadowColor = base;
    ctx.shadowBlur = isActive ? this.cellSize * (0.28 + pulsePhase * 0.12) : this.cellSize * 0.16;

    const sphereGrad = ctx.createRadialGradient(
      x - sphereR * 0.28,
      y - sphereR * 0.32,
      sphereR * 0.05,
      x,
      y,
      sphereR,
    );
    sphereGrad.addColorStop(0, '#FFFFFF');
    sphereGrad.addColorStop(0.25, light);
    sphereGrad.addColorStop(0.7, base);
    sphereGrad.addColorStop(1, base);
    ctx.fillStyle = sphereGrad;

    ctx.beginPath();
    ctx.arc(x, y, sphereR, 0, Math.PI * 2);
    ctx.fill();

    if (isActive) {
      ctx.globalAlpha = 0.35 + pulsePhase * 0.25;
      ctx.strokeStyle = light;
      ctx.lineWidth = Math.max(1.5, this.cellSize * 0.022);
      ctx.beginPath();
      ctx.arc(x, y, sphereR * (1.15 + pulsePhase * 0.08), 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawGearTornChainOverlay(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    base: string,
    alphaMul: number,
  ): void {
    const { ctx } = this;
    const p1 = this.posToPixel([x1, y1]);
    const p2 = this.posToPixel([x2, y2]);
    const isHorizontal = x1 !== x2;
    const linkR = this.cellSize * 0.08;
    const gap = this.cellSize * 0.12;

    ctx.save();
    ctx.globalAlpha = (1 - alphaMul) * 0.85;
    ctx.strokeStyle = base;
    ctx.lineWidth = Math.max(1.5, this.cellSize * 0.018);
    ctx.setLineDash([linkR * 0.6, gap]);

    if (isHorizontal) {
      const midY = p1.y + (p2.y - p1.y) * 0.5 + this.cellSize * 0.04;
      ctx.beginPath();
      ctx.moveTo(p1.x + gap, midY);
      ctx.lineTo(p2.x - gap, midY);
      ctx.stroke();
    } else {
      const midX = p1.x + (p2.x - p1.x) * 0.5 + this.cellSize * 0.04;
      ctx.beginPath();
      ctx.moveTo(midX, p1.y + gap);
      ctx.lineTo(midX, p2.y - gap);
      ctx.stroke();
    }

    ctx.setLineDash([]);
    ctx.restore();
  }

  private drawGearEndpointRim(
    colorId: string,
    x: number,
    y: number,
    radius: number,
    base: string,
    light: string,
    brass: string,
    brassHi: string,
    copper: string,
    gold: string,
  ): void {
    const { ctx } = this;
    if (drawGearRimSprite(ctx, colorId, x, y, radius)) {
      return;
    }
    const rimR = radius * 0.82;
    const lw = Math.max(2, this.cellSize * 0.028);
    const lwThin = Math.max(1, this.cellSize * 0.018);

    switch (colorId) {
      case 'c1': {
        ctx.strokeStyle = brassHi;
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.arc(x, y, rimR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = brass;
        ctx.lineWidth = lwThin;
        for (let i = 0; i < 4; i++) {
          const a = (i * Math.PI) / 2 + Math.PI / 4;
          ctx.beginPath();
          ctx.moveTo(x + Math.cos(a) * radius * 0.42, y + Math.sin(a) * radius * 0.42);
          ctx.lineTo(x + Math.cos(a) * rimR * 0.95, y + Math.sin(a) * rimR * 0.95);
          ctx.stroke();
        }
        break;
      }
      case 'c2': {
        ctx.strokeStyle = brassHi;
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.arc(x, y, rimR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = base;
        ctx.lineWidth = lwThin;
        ctx.beginPath();
        ctx.arc(x, y, radius * 0.72, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = light;
        for (let i = 0; i < 4; i++) {
          const a = (i * Math.PI) / 2;
          ctx.beginPath();
          ctx.arc(
            x + Math.cos(a) * radius * 0.58,
            y + Math.sin(a) * radius * 0.58,
            radius * 0.07,
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }
        break;
      }
      case 'c3': {
        ctx.strokeStyle = gold;
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.arc(x, y, rimR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = gold;
        ctx.lineWidth = lwThin;
        const clip = radius * 0.22;
        for (let i = 0; i < 4; i++) {
          const a = (i * Math.PI) / 2;
          const cx = x + Math.cos(a) * rimR * 0.88;
          const cy = y + Math.sin(a) * rimR * 0.88;
          ctx.beginPath();
          ctx.moveTo(cx - clip, cy);
          ctx.lineTo(cx + clip, cy);
          ctx.moveTo(cx, cy - clip);
          ctx.lineTo(cx, cy + clip);
          ctx.stroke();
        }
        break;
      }
      case 'c4': {
        ctx.strokeStyle = brassHi;
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.arc(x, y, rimR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
        ctx.lineWidth = lwThin;
        ctx.beginPath();
        ctx.arc(x - radius * 0.12, y - radius * 0.12, rimR * 0.92, -Math.PI * 0.15, Math.PI * 0.55);
        ctx.stroke();
        break;
      }
      case 'c5': {
        ctx.strokeStyle = brass;
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.arc(x, y, rimR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = base;
        ctx.lineWidth = lwThin;
        ctx.beginPath();
        ctx.arc(x, y, radius * 0.68, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = light;
        ctx.lineWidth = Math.max(1, lwThin * 0.85);
        ctx.beginPath();
        ctx.arc(x, y, radius * 0.58, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
      case 'c6': {
        ctx.strokeStyle = copper;
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.arc(x, y, rimR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = gold;
        ctx.lineWidth = Math.max(1, lwThin * 0.75);
        for (let i = 0; i < 6; i++) {
          const a = (i * Math.PI) / 3;
          const r1 = radius * 0.62;
          const r2 = radius * 0.78;
          ctx.beginPath();
          ctx.moveTo(x + Math.cos(a) * r1, y + Math.sin(a) * r1);
          ctx.quadraticCurveTo(
            x + Math.cos(a + 0.25) * r2,
            y + Math.sin(a + 0.25) * r2,
            x + Math.cos(a + 0.5) * r1,
            y + Math.sin(a + 0.5) * r1,
          );
          ctx.stroke();
        }
        break;
      }
      default: {
        ctx.strokeStyle = brassHi;
        ctx.lineWidth = lw;
        ctx.beginPath();
        ctx.arc(x, y, rimR, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }

  /** Blends a hex color toward black by `amount` (0 = unchanged, 1 = black) —
   *  used by `cyberpunk`/`goth` to get a "dark iron"/"dark PCB" base tint of
   *  the pair's own color instead of a flat near-black that would make every
   *  pair look the same. */
  private darkenColor(hex: string, amount: number): string {
    const n = hex.replace('#', '');
    const r = Math.round(parseInt(n.slice(0, 2), 16) * (1 - amount));
    const g = Math.round(parseInt(n.slice(2, 4), 16) * (1 - amount));
    const b = Math.round(parseInt(n.slice(4, 6), 16) * (1 - amount));
    const toHex = (v: number) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private themeColor(varName: string, fallback: string): string {
    const decl =
      this.styleDecl ?? (this.styleDecl = getComputedStyle(document.documentElement));
    const value = decl.getPropertyValue(varName).trim();
    return value || fallback;
  }

  private roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ): void {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.arcTo(x + w, y, x + w, y + radius, radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
    ctx.lineTo(x + radius, y + h);
    ctx.arcTo(x, y + h, x, y + h - radius, radius);
    ctx.lineTo(x, y + radius);
    ctx.arcTo(x, y, x + radius, y, radius);
    ctx.closePath();
  }
}
